﻿import { calculateImagePlacement } from './image-placement';
import type { ImageZoomMode } from './image-placement';
import {
    FeaturedImageLayerOwner,
    FeaturedImageLoadOwner,
    isFeaturedImageLoadCancellation,
    releaseFeaturedImageSource
} from './featured-image-loading';
import type { FeaturedImageLoadOperation } from './featured-image-loading';
import { shouldRequestFeaturedImageOriginal } from './featured-image-quality';
import {
    createHomepageTileOccurrenceKey,
    reconcileHomepageTileOccurrences,
    resolveHomepageTileActivation
} from './homepage-grid';
import type { HomepageTileOccurrence } from './homepage-grid';
import {
    AutomaticFeatureTimer,
    defaultHomepagePlaybackPlaying,
    ExclusiveAsyncTask,
    getHomepagePlaybackControlPresentation,
    hasCollectionChanged,
    PausableDelay,
    runRefreshCycle
} from './homepage-background-work';
import {
    createFeaturedImagePath,
    createFeaturedImageHistoryState,
    createFeaturedImageSlug,
    featuredImageSlugMatchesTitle,
    findCollectionSlug,
    getCollectionSlugFromPath,
    getFeaturedImageHistoryKey,
    getFeaturedImageSlugFromPath,
    isBackForwardPageShow
} from './homepage-navigation';
import { calculateLoopingGridLayout } from './looping-grid-layout';
import { createPanelCollapseButton, initPanelCollapse } from './panel-collapse';
import { createPhotoInsightsTracker, sendPhotoPresentation } from './photo-insights';
import type {
    PhotoPresentation,
    PhotoPresentationEndReason,
    PhotoPresentationFlag,
    PhotoPresentationTrigger
} from './photo-insights';
import {
    createCheckoutRequestBody,
    formatPrintPrice,
    formatSiteString,
    getCheckoutSessionIdFromSearch,
    normalizeCheckoutStatus,
    normalizeShopConfig
} from './print-checkout';
import type { CheckoutStatus, ShopConfig } from './print-checkout';
import { FeaturedImageCycle } from './random-image-selection';
import { createSiteTelemetry } from './site-telemetry';
import { siteStrings } from './site-strings';
import { createUrlSlug } from './slug';
import { createWallExposureSampler, installWallExposureSampler, sendPhotoExposure } from './wall-exposure';
import { initializeWindChimeControls } from './wind-chime-controls';
import type { ImageBounds, Rectangle, ViewportTransform } from './site-animation';
import {
    advanceLoopOffset,
    calculateFeaturedImageFitScale,
    calculateFeaturedImageFrameTransform,
    calculateFeaturedImagePanTransform,
    calculateFeaturedImagePinchTransform,
    calculateFeaturedImageWheelTransform,
    calculatePinchItemsPerRow,
    calculateStoppingDistance,
    calculateVelocityTransitionDistance,
    calculateWheelItemsPerRow,
    calculateWheelMomentumVelocity,
    approachVelocity,
    decelerateVelocity,
    featuredPhotoCaptionFadeMilliseconds,
    gridZoomResetMilliseconds,
    isRectangleVisible,
    normalizeWheelDelta,
    selectFeatureCandidateRowIndexes,
    selectFeatureCaptionPosition,
    selectFeatureCenteringAdjustment,
    selectFeatureReturnRectangle,
    userFeatureHoldMilliseconds
} from './site-animation';
import './app.css';

type SiteConfig = {
    collectionSlug: string | null;
    zoomMode: ImageZoomMode;
    scrollPixelsPerSecond: number;
    featureIntervalSeconds: number;
    featureExpandMilliseconds: number;
    featureHoldMilliseconds: number;
    featureCaptionHoldMilliseconds: number;
    featureFadeMilliseconds: number;
    collectionRefreshSeconds: number;
    shop: ShopConfig;
};

type SiteRoute = 'home' | 'about' | 'subscribe' | 'contact' | 'rights' | 'prints' | 'return-policy' | 'order-thanks';

const siteRoutePaths: Record<SiteRoute, string> = {
    home: '/',
    about: '/about',
    subscribe: '/subscribe',
    contact: '/contact',
    rights: '/rights',
    prints: '/prints',
    'return-policy': '/return-policy',
    'order-thanks': '/order/thanks'
};
const orderThanksMaximumAttempts = 6;
const orderThanksPollMilliseconds = 2000;

class SiteConnectionError extends Error {
    constructor(cause: unknown) {
        super(siteStrings.connectionFailed, { cause });
        this.name = 'SiteConnectionError';
    }
}

export type PublicPhoto = {
    imageKey: string;
    title: string | null;
    description: string | null;
    altText: string | null;
    thumbnailImageUrl: string;
    imageUrl: string;
    fullResolutionImageUrl: string;
    displayResolutionImageUrl: string | null;
    previewWidth: number;
    previewHeight: number;
    fullResolutionWidth: number;
    fullResolutionHeight: number;
    poiX: number;
    poiY: number;
    sortOrder: number;
};

type PublicCollection = {
    name: string;
    slug: string;
    lastModifiedUtc: string;
    images: PublicPhoto[];
};

type AdminCollectionSummary = {
    name: string;
    slug: string;
    // Visibility flag returned by the admin collection summaries endpoint. The public collections endpoint omits
    // it, so a missing value is treated as public.
    isPublic?: boolean;
};

type RenderedTile = {
    photo: PublicPhoto;
    element: HTMLElement;
    image: HTMLImageElement;
    rowKey: string;
    /** A private photograph opened by link: it floats over the wall without a tile, so it fades away on close. */
    standalone?: boolean;
};

type RenderInputs = {
    viewportWidth: number;
    viewportHeight: number;
    targetItemsPerRow: number;
    collectionRevision: string;
};

const horizontalMarginPixels = 24;
const gapPixels = 10;
const desktopTargetItemsPerRow = 5;
const desktopReferenceWidthPixels = 1600;
const phoneTargetItemsPerRow = 2;
const portraitTargetItemsPerRow = 3;
const minimumTargetItemsPerRow = 4;
const maximumTargetItemsPerRow = 5;
const fallbackAspectRatio = 8 / 5;
const dragThresholdPixels = 4;
const minimumMomentumVelocity = 120;
const maximumMomentumVelocity = 1_600;
const momentumDeceleration = 2_400;
const wheelMomentumPerPixel = 4;
const minimumPinchItemsPerRow = 2;
const maximumPinchItemsPerRow = 8;
const maximumFeaturedImageScale = 4;
const cornerAnimationKeyframeCount = 20;
const contactToastDurationMilliseconds = 4_000;
const featuredPhotoCloseFadeMilliseconds = 500;
const contentPanelFadeMilliseconds = 500;
const featuredSwipeDistancePixels = 60;
const featuredSwipeDurationMilliseconds = 700;
const featuredSwipeClickSuppressionMilliseconds = 400;
const featuredNavigationSlideMilliseconds = 360;
const automaticScrollTransitionSeconds = 1;
// Full fade-out plus fade-in time for a collection change; each direction gets half, so the swap never cuts hard.
const collectionTransitionMilliseconds = 600;
const mobilePhone = isMobilePhone();
const siteTelemetry = createSiteTelemetry();
const photoInsights = createPhotoInsightsTracker({
    now: () => performance.now(),
    getIdentity: () => siteTelemetry.getIdentity(),
    send: sendPhotoPresentation
});
// Wall exposure is sampled while the homepage wall is on screen, which it is not while a photograph is presented.
const wallExposure = createWallExposureSampler({
    now: () => performance.now(),
    isWallShowing: () => currentRoute === 'home'
        && !document.hidden
        && featureRequest == null
        && outgoingFeatureRequests.length === 0,
    getTiles: () => renderedTiles.map(tile => ({
        imageKey: tile.photo.imageKey,
        title: tile.photo.title,
        getRectangle: () => tile.element.getBoundingClientRect()
    })),
    getViewportSize: () => ({ width: window.innerWidth, height: window.innerHeight }),
    getIdentity: () => siteTelemetry.getIdentity(),
    send: sendPhotoExposure
});

const wall = requireElement('photo-wall', HTMLElement);
const track = requireElement('photo-track', HTMLElement);
const statusSurface = requireElement('site-status', HTMLElement);
const siteHeader = requireElement('site-header', HTMLElement);
const collectionTitle = requireElement('collection-title', HTMLHeadingElement);
const collectionTitleText = requireElement('collection-title-text', HTMLSpanElement);
const collectionNavigation = requireElement('collection-navigation', HTMLElement);
const collectionNavigationList = requireElement('collection-navigation-list', HTMLUListElement);
const featureLayer = requireElement('feature-layer', HTMLElement);
const pageLayer = requireElement('page-layer', HTMLElement);
const aboutPage = requireElement('about-page', HTMLElement);
const aboutClose = requireElement('about-close', HTMLButtonElement);
const subscribePage = requireElement('subscribe-page', HTMLElement);
const subscribeClose = requireElement('subscribe-close', HTMLButtonElement);
const subscribeFormView = requireElement('subscribe-form-view', HTMLElement);
const subscribeForm = requireElement('subscribe-form', HTMLFormElement);
const subscribeWebsite = requireElement('subscribe-website', HTMLInputElement);
const subscribeEmail = requireElement('subscribe-email', HTMLInputElement);
const subscribeSubmit = requireElement('subscribe-submit', HTMLButtonElement);
const subscribeStatus = requireElement('subscribe-status', HTMLElement);
const subscribeResultView = requireElement('subscribe-result-view', HTMLElement);
const subscribeResult = requireElement('subscribe-result', HTMLElement);
const unsubscribeView = requireElement('unsubscribe-view', HTMLElement);
const unsubscribeStatus = requireElement('unsubscribe-status', HTMLElement);
const unsubscribeSubmit = requireElement('unsubscribe-submit', HTMLButtonElement);
const contactPage = requireElement('contact-page', HTMLElement);
const contactClose = requireElement('contact-close', HTMLButtonElement);
const contactForm = requireElement('contact-form', HTMLFormElement);
const contactWebsite = requireElement('contact-website', HTMLInputElement);
const contactEmail = requireElement('contact-email', HTMLInputElement);
const contactSubject = requireElement('contact-subject', HTMLInputElement);
const contactBody = requireElement('contact-body', HTMLTextAreaElement);
const contactSend = requireElement('contact-send', HTMLButtonElement);
const contactStatus = requireElement('contact-status', HTMLElement);
const rightsPage = requireElement('rights-page', HTMLElement);
const rightsClose = requireElement('rights-close', HTMLButtonElement);
const printsPage = requireElement('prints-page', HTMLElement);
const printsClose = requireElement('prints-close', HTMLButtonElement);
const returnPolicyPage = requireElement('return-policy-page', HTMLElement);
const returnPolicyClose = requireElement('return-policy-close', HTMLButtonElement);
const printsUnavailable = requireElement('prints-unavailable', HTMLElement);
const printsDetails = requireElement('prints-details', HTMLElement);
const orderThanksPage = requireElement('order-thanks-page', HTMLElement);
const orderThanksClose = requireElement('order-thanks-close', HTMLButtonElement);
const orderThanksStatus = requireElement('order-thanks-status', HTMLElement);
const orderThanksItems = requireElement('order-thanks-items', HTMLUListElement);
const orderThanksDetails = requireElement('order-thanks-details', HTMLElement);
const contactToast = requireElement('contact-toast', HTMLElement);
const homepagePlaybackToggle = requireElement('homepage-playback-toggle', HTMLButtonElement);
const adminLink = requireElement('admin-link', HTMLAnchorElement);
const adminSeparator = requireElement('admin-separator', HTMLElement);
const siteRouteLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>('[data-site-route]'));

let config: SiteConfig | null = null;
let orderThanksGeneration = 0;
let collection: PublicCollection | null = null;
let renderedTiles: RenderedTile[] = [];
let renderedTilesByOccurrenceKey = new Map<string, RenderedTile>();
const sequenceElements = new Map<number, HTMLElement>();
let lastRenderInputs: RenderInputs | null = null;
let sequenceHeight = 0;
let resizeFrameId = 0;
let scrollOffset = 0;
let automaticScrollVelocity = 0;
let lastAnimationTimestamp = 0;
let isHomepagePlaybackPlaying = defaultHomepagePlaybackPlaying;
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
let lastFeaturedImageKey: string | null = null;
const featuredImageCycle = new FeaturedImageCycle<string>();
let suppressNextTileClick = false;
let suppressNextFeatureDismiss = false;
// A collection switch replaces every photograph on the wall, so the navigation is made inert while the
// replacement collection loads instead of letting a second switch queue behind the first.
let collectionNavigationBusy = false;
let momentumVelocity = 0;
let currentRoute = getSiteRoute(window.location.pathname);
let contactToastTimeoutId: number | null = null;
let contentPanelTransitionAnimation: Animation | null = null;
let contentPanelTransitionGeneration = 0;
let unsubscribeToken: string | null = null;
const refreshTask = new ExclusiveAsyncTask();
let initializationCompleted = false;
let adminCollectionSummaries: AdminCollectionSummary[] = [];
let adminCollectionSlug: string | null = null;
let publicCollections: AdminCollectionSummary[] = [];
let linkedCollectionSlug: string | null = null;
let pendingDirectCollectionSlug: string | null = null;

type WallDrag = {
    pointerId: number;
    startingY: number;
    startingOffset: number;
    hasMoved: boolean;
    lastY: number;
    lastTimestamp: number;
    velocity: number;
};

let wallDrag: WallDrag | null = null;

type TouchPoint = {
    x: number;
    y: number;
};

type PinchGesture = {
    pointerIds: readonly [number, number];
    startingDistance: number;
    startingItemsPerRow: number;
};

const activeTouchPointers = new Map<number, TouchPoint>();
let pinchGesture: PinchGesture | null = null;
let pinchAwaitingRelease = false;
let pinchItemsPerRow: number | null = null;
let zoomRenderFrameId = 0;
let zoomResetTimeoutId: number | null = null;

type FeatureSource = 'automatic' | 'user';

type FeaturedImagePinch = {
    kind: 'pinch';
    pointerIds: readonly [number, number];
    startingCenter: TouchPoint;
    startingDistance: number;
    startingTransform: ViewportTransform;
};

type FeaturedImagePan = {
    kind: 'pan';
    pointerId: number;
    startingPoint: TouchPoint;
    startingTransform: ViewportTransform;
    hasMoved: boolean;
};

type FeaturedImageSwipe = {
    kind: 'swipe';
    pointerId: number;
    startingPoint: TouchPoint;
    startingTimestamp: number;
    isHorizontal: boolean;
};

type FeaturedImageGestureState = {
    content: HTMLElement;
    frame: HTMLElement;
    cornerRadius: number;
    enabled: boolean;
    imageBounds: ImageBounds;
    minimumScale: number;
    pointers: Map<number, TouchPoint>;
    transform: ViewportTransform;
    gesture: FeaturedImagePinch | FeaturedImagePan | FeaturedImageSwipe | null;
    suppressDismiss: boolean;
    quality: FeaturedImageQualityState;
};

type FeaturedImageQualityState = {
    frame: HTMLElement;
    activeImage: HTMLImageElement;
    preferredImage: HTMLImageElement;
    preferredImageUrl: string;
    preferredImageIsOriginal: boolean;
    preferredStatus: 'idle' | 'loading' | 'ready' | 'displayed' | 'failed';
    preferredOperation: FeaturedImageLoadOperation | null;
    preferredRevealSignal: AbortSignal | null;
    originalImage: HTMLImageElement | null;
    originalAttempt: object | null;
    originalOperation: FeaturedImageLoadOperation | null;
    fullResolutionImageUrl: string;
    availableWidth: number;
    availableHeight: number;
    isOriginal: boolean;
    originalRequested: boolean;
};

type FeatureRequest = {
    source: FeatureSource;
    imageKey: string;
    sourceTile: RenderedTile;
    hasActiveHistoryEntry: boolean;
    isAwaitingHistoryDismissal: boolean;
    pauseGridScrolling: boolean;
    controller: AbortController;
    imageLayers: FeaturedImageLayerOwner;
    imageLoads: FeaturedImageLoadOwner;
    element: HTMLElement | null;
    caption: HTMLElement | null;
    captionAnimation: Animation | null;
    captionHoldTimeoutId: number | null;
    closeButton: HTMLButtonElement | null;
    closeButtonAnimation: Animation | null;
    animations: Animation[];
    closingAnimations: Animation[];
    chromeFadeAnimations: Animation[];
    transitionGeneration: number;
    isClosing: boolean;
    isReopening: boolean;
    gestureState: FeaturedImageGestureState | null;
    featureHoldDelay: PausableDelay | null;
    resetHoldTimer: (() => void) | null;
    /** The photo insights record for this presentation; null for untitled photographs or once it has ended. */
    insights: PhotoPresentation | null;
    /** Why the presentation is closing, when it is not simply closed; reopening clears it. */
    insightsEndReason: PhotoPresentationEndReason | null;
};

let featureRequest: FeatureRequest | null = null;
let outgoingFeatureRequests: FeatureRequest[] = [];
let featureCloseButton: HTMLButtonElement | null = null;
let featurePreviousNavButton: HTMLButtonElement | null = null;
let featureNextNavButton: HTMLButtonElement | null = null;

/// Sets a photo insights flag on the presentation that owns the gesture state, if it is still the open one.
function markFeaturedImageInsight(state: FeaturedImageGestureState, flag: PhotoPresentationFlag): void {
    const request = featureRequest;
    if (request?.gestureState === state) request.insights?.mark(flag);
}

/// Sends the presentation's photo insights record (once) and detaches it from the request.
function endFeatureInsights(request: FeatureRequest, reason: PhotoPresentationEndReason): void {
    request.insights?.end(reason);
    request.insights = null;
    request.insightsEndReason = null;
}

function createCornerRadiusKeyframes(
    startingScale: number,
    endingScale: number,
    startingScreenRadius: number,
    endingScreenRadius: number
): Keyframe[] {
    return Array.from({ length: cornerAnimationKeyframeCount + 1 }, (_, index) => {
        const offset = index / cornerAnimationKeyframeCount;
        const scale = startingScale + (endingScale - startingScale) * offset;
        const screenRadius = startingScreenRadius + (endingScreenRadius - startingScreenRadius) * offset;
        return {
            borderRadius: `${screenRadius / scale}px`,
            offset
        };
    });
}

function getTelemetryPath(route: SiteRoute): string {
    if (route !== 'home') return siteRoutePaths[route];
    return getFeaturedImageSlugFromPath(window.location.pathname) == null
        ? '/'
        : window.location.pathname;
}

function showContactToast(message: string): void {
    if (contactToastTimeoutId != null) window.clearTimeout(contactToastTimeoutId);
    contactToast.textContent = message;
    contactToast.hidden = false;
    contactToastTimeoutId = window.setTimeout(() => {
        contactToastTimeoutId = null;
        contactToast.hidden = true;
    }, contactToastDurationMilliseconds);
}

function getSiteRoute(pathname: string): SiteRoute {
    const normalizedPath = pathname.toLowerCase().replace(/\/+$/, '') || '/';
    if (normalizedPath === '/about') return 'about';
    if (normalizedPath === '/subscribe' || normalizedPath.startsWith('/subscribe/')) return 'subscribe';
    if (normalizedPath === '/contact') return 'contact';
    if (normalizedPath === '/rights') return 'rights';
    if (normalizedPath === '/prints') return 'prints';
    if (normalizedPath === '/return-policy') return 'return-policy';
    if (normalizedPath === '/order/thanks') return 'order-thanks';
    return 'home';
}

function updateDocumentTitle(): void {
    const showCollectionTitle = currentRoute === 'home' && collection != null;
    collectionTitleText.textContent = showCollectionTitle ? collection?.name ?? '' : '';
    // A temporary deep-link view into a non-public collection cannot be represented by the public-only navigation,
    // so show the label instead. Otherwise, administrators always get the collection links and anonymous visitors
    // only when more than one public collection exists.
    const isLinkedPrivateOverride = linkedCollectionSlug != null;
    const showCollectionNavigation = currentRoute === 'home'
        && !isLinkedPrivateOverride
        && (adminCollectionSummaries.length > 0 || publicCollections.length > 1);
    collectionTitle.hidden = !showCollectionTitle || showCollectionNavigation;
    collectionNavigation.hidden = !showCollectionNavigation;
    if (showCollectionNavigation) updateCollectionNavigationActiveState();

    if (currentRoute === 'about') {
        document.title = 'About | Joseph Edwin Gilbertie Photography';
        return;
    }
    if (currentRoute === 'contact') {
        document.title = 'Contact | Joseph Edwin Gilbertie Photography';
        return;
    }
    if (currentRoute === 'subscribe') {
        document.title = 'Subscribe | Joseph Edwin Gilbertie Photography';
        return;
    }
    if (currentRoute === 'rights') {
        document.title = 'Rights | Joseph Edwin Gilbertie Photography';
        return;
    }
    if (currentRoute === 'order-thanks') {
        document.title = 'Thank you | Joseph Edwin Gilbertie Photography';
        return;
    }
    if (currentRoute === 'prints') {
        document.title = 'Prints | Joseph Edwin Gilbertie Photography';
        return;
    }
    if (currentRoute === 'return-policy') {
        document.title = 'Return policy | Joseph Edwin Gilbertie Photography';
        return;
    }

    // A direct photo URL names one photograph, so the tab title must name that photograph instead of the
    // collection: crawlers and share previews that resolve `/photo/{slug}` read the title the URL implies.
    // featuredImageSlugMatchesTitle mirrors the server's PhotoSlug rules, so the client-rendered title cannot
    // disagree with the title PhotoPageRenderer emits for the same URL.
    const photoSlug = getFeaturedImageSlugFromPath(window.location.pathname);
    if (photoSlug != null) {
        // A standalone private photograph is not on the wall, so it is found through its presentation.
        const standalonePhoto = featureRequest?.sourceTile.standalone === true ? [featureRequest.sourceTile.photo] : [];
        const photo = [...(collection?.images ?? []), ...standalonePhoto].find((image) => image.title != null
            && image.title.trim().length > 0
            && featuredImageSlugMatchesTitle(photoSlug, image.title));
        if (photo?.title != null) {
            document.title = `${photo.title} | Joseph Edwin Gilbertie Photography`;
            // The photo's caption now carries the sole H1, so hide the fixed collection heading while the
            // photograph is open to avoid two H1s and an inverted hierarchy. Closing the photograph restores it.
            collectionTitleText.textContent = '';
            collectionTitle.hidden = true;
            return;
        }
    }

    document.title = collection == null
        ? 'Joseph Edwin Gilbertie Photography'
        : `${collection.name} | Joseph Edwin Gilbertie Photography`;
}

async function transitionContentPanel(route: SiteRoute): Promise<void> {
    const generation = ++contentPanelTransitionGeneration;
    contentPanelTransitionAnimation?.cancel();
    contentPanelTransitionAnimation = null;

    const targetPanel = route === 'about'
        ? aboutPage
        : route === 'subscribe'
            ? subscribePage
            : route === 'contact'
                ? contactPage
                : route === 'rights'
                    ? rightsPage
                    : route === 'order-thanks'
                        ? orderThanksPage
                        : route === 'prints'
                            ? printsPage
                            : route === 'return-policy'
                                ? returnPolicyPage
                                : null;
    const currentPanel = !aboutPage.hidden
        ? aboutPage
        : !subscribePage.hidden
            ? subscribePage
            : !contactPage.hidden
                ? contactPage
                : !rightsPage.hidden
                    ? rightsPage
                    : !orderThanksPage.hidden
                        ? orderThanksPage
                        : !printsPage.hidden
                            ? printsPage
                            : !returnPolicyPage.hidden
                                ? returnPolicyPage
                                : null;
    if (currentPanel === targetPanel) return;

    const duration = reducedMotionQuery.matches ? 0 : contentPanelFadeMilliseconds;
    if (currentPanel != null) {
        const fadeOutAnimation = currentPanel.animate(
            [{ opacity: getComputedStyle(currentPanel).opacity }, { opacity: 0 }],
            { duration, easing: 'ease-in', fill: 'forwards' });
        contentPanelTransitionAnimation = fadeOutAnimation;
        try {
            await fadeOutAnimation.finished;
        } catch {
            return;
        }
        if (contentPanelTransitionGeneration !== generation) return;
        currentPanel.hidden = true;
        fadeOutAnimation.cancel();
        contentPanelTransitionAnimation = null;
    }

    if (targetPanel == null) {
        pageLayer.hidden = true;
        return;
    }

    pageLayer.hidden = false;
    targetPanel.hidden = false;
    const fadeInAnimation = targetPanel.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration, easing: 'ease-out', fill: 'forwards' });
    contentPanelTransitionAnimation = fadeInAnimation;
    try {
        await fadeInAnimation.finished;
    } catch {
        return;
    }
    if (contentPanelTransitionGeneration !== generation) return;
    fadeInAnimation.cancel();
    contentPanelTransitionAnimation = null;
}

async function showSiteRoute(
    route: SiteRoute,
    addHistoryEntry: boolean,
    trackPageView = true
): Promise<void> {
    currentRoute = route;

    for (const link of siteRouteLinks) {
        const isCurrent = link.dataset.siteRoute === route;
        if (isCurrent) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
    }

    if (addHistoryEntry) {
        const pathname = siteRoutePaths[route];
        if (window.location.pathname !== pathname) window.history.pushState(null, '', pathname);
    }
    if (trackPageView) siteTelemetry.trackPageView(getTelemetryPath(route));

    if (route === 'home') {
        scheduleNextAutomaticFeature();
    } else {
        cancelAutomaticFeatureTimer();
        cancelFeaturePresentationImmediately();
    }
    updateDocumentTitle();
    if (route === 'subscribe') await updateSubscriptionView();
    if (route === 'order-thanks') void updateOrderThanksView();
    await transitionContentPanel(route);
}

function setOrderThanksStatus(message: string, kind: 'status' | 'success' | 'error'): void {
    orderThanksStatus.textContent = message;
    orderThanksStatus.dataset.kind = kind;
}

function renderOrderThanksItems(status: CheckoutStatus): void {
    orderThanksItems.replaceChildren(...status.items.map(item => {
        const element = document.createElement('li');
        element.textContent = item.quantity > 1 ? `${item.title} ×${item.quantity}` : item.title;
        return element;
    }));
    orderThanksItems.hidden = status.items.length === 0;
    orderThanksDetails.textContent = formatSiteString(siteStrings.orderThanksDetails, { days: status.shipWithinBusinessDays });
    orderThanksDetails.hidden = false;
}

/**
 * Shows the outcome of the Stripe redirect. The order itself is created by the server's webhook, which can arrive a
 * moment after the buyer, so a settled-but-unrecorded payment is polled briefly before falling back to "processing".
 */
async function updateOrderThanksView(): Promise<void> {
    const generation = ++orderThanksGeneration;
    orderThanksItems.replaceChildren();
    orderThanksItems.hidden = true;
    orderThanksDetails.hidden = true;
    setOrderThanksStatus(siteStrings.orderThanksChecking, 'status');

    const sessionId = getCheckoutSessionIdFromSearch(window.location.search);
    if (sessionId == null) {
        setOrderThanksStatus(siteStrings.orderThanksInvalid, 'error');
        return;
    }

    let processing: CheckoutStatus | null = null;
    for (let attempt = 0; attempt < orderThanksMaximumAttempts; attempt++) {
        if (attempt > 0) await new Promise(resolve => window.setTimeout(resolve, orderThanksPollMilliseconds));
        if (generation !== orderThanksGeneration || currentRoute !== 'order-thanks') return;

        let status: CheckoutStatus | null = null;
        try {
            const response = await fetch(`/api/checkout/sessions/${encodeURIComponent(sessionId)}/status`, {
                headers: { Accept: 'application/json' }
            });
            if (response.status === 400 || response.status === 404) {
                if (generation === orderThanksGeneration) setOrderThanksStatus(siteStrings.orderThanksInvalid, 'error');
                return;
            }
            if (response.ok) status = normalizeCheckoutStatus(await response.json());
        } catch {
            status = null;
        }
        if (generation !== orderThanksGeneration) return;
        if (status == null) continue;

        if (status.status === 'paid') {
            renderOrderThanksItems(status);
            setOrderThanksStatus(formatSiteString(siteStrings.orderThanksPaid, { orderNumber: status.orderNumber ?? '' }), 'success');
            return;
        }
        if (status.status === 'unpaid') {
            setOrderThanksStatus(siteStrings.orderThanksUnpaid, 'error');
            return;
        }
        processing = status;
    }

    if (processing != null) {
        renderOrderThanksItems(processing);
        setOrderThanksStatus(siteStrings.orderThanksProcessing, 'success');
    } else {
        setOrderThanksStatus(siteStrings.orderThanksFailed, 'error');
    }
}

/**
 * Asks the server to open a Stripe Checkout page for one print of the photograph and sends the browser there. The
 * server resolves the photograph and sets the price; only the title slug and the session's channel are sent.
 */
async function startPrintCheckout(
    title: string,
    button: HTMLButtonElement,
    status: HTMLElement,
    isCurrent: () => boolean,
    releaseFeature: () => void,
    beforeRedirect: () => void
): Promise<void> {
    if (button.disabled) return;
    button.disabled = true;
    status.textContent = siteStrings.orderPrintOpening;
    const fail = (message: string): void => {
        if (isCurrent()) status.textContent = message;
        button.disabled = false;
        releaseFeature();
    };
    try {
        const response = await fetch('/api/checkout/sessions', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(createCheckoutRequestBody(
                createFeaturedImageSlug(title),
                siteTelemetry.getChannel(),
                siteTelemetry.getReferrerHost()))
        });
        if (!response.ok) {
            fail(await readApiError(response, siteStrings.orderPrintFailed));
            return;
        }
        const payload: unknown = await response.json();
        const url = payload != null && typeof payload === 'object' ? (payload as Record<string, unknown>).url : null;
        if (typeof url !== 'string' || !url.startsWith('https://')) throw new Error('The checkout URL was missing.');
        beforeRedirect();
        window.location.assign(url);
        // Recover after a pause in case the page is restored from the back/forward cache instead of reloaded.
        window.setTimeout(() => {
            if (isCurrent()) status.textContent = '';
            button.disabled = false;
            releaseFeature();
        }, 5000);
    } catch {
        fail(siteStrings.orderPrintFailed);
    }
}

/**
 * Keeps the featured photograph and its caption on screen while checkout opens, as pausing playback does, so the
 * buyer is not dropped back to the grid mid-request. The returned callback restores the normal hold and fade.
 */
function holdFeatureOpen(request: FeatureRequest): () => void {
    request.featureHoldDelay?.pause();
    cancelFeatureCaptionHoldTimer(request);
    request.captionAnimation?.cancel();
    request.captionAnimation = null;
    request.caption?.style.setProperty('opacity', '1');
    let released = false;
    return () => {
        if (released) return;
        released = true;
        if (featureRequest !== request || request.isClosing || !isHomepagePlaybackPlaying) return;
        request.featureHoldDelay?.resume();
        request.resetHoldTimer?.();
        if (request.caption != null && request.captionAnimation == null) scheduleFeatureCaptionFadeOut(request);
    };
}

async function readApiError(response: Response, fallback: string): Promise<string> {
    try {
        const payload: unknown = await response.json();
        if (payload != null && typeof payload === 'object') {
            const error = (payload as Record<string, unknown>).error;
            if (typeof error === 'string' && error.length > 0) return error;
        }
    } catch {
        // The fallback message is used when the server does not return JSON.
    }
    return fallback;
}

async function updateSubscriptionView(): Promise<void> {
    subscribeFormView.hidden = true;
    subscribeResultView.hidden = true;
    unsubscribeView.hidden = true;
    unsubscribeSubmit.hidden = true;
    unsubscribeToken = null;

    const normalizedPath = window.location.pathname.toLowerCase().replace(/\/+$/, '');
    const fragmentParameters = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    if (normalizedPath === '/subscribe/confirm') {
        subscribeResult.textContent = siteStrings.subscriptionSubmitting;
        subscribeResult.dataset.kind = 'status';
        subscribeResultView.hidden = false;
        const token = fragmentParameters.get('token');
        if (token == null || token.length === 0) {
            subscribeResult.dataset.kind = 'error';
            subscribeResult.textContent = siteStrings.subscriptionConfirmationInvalid;
            return;
        }

        let result = 'invalid';
        try {
            const response = await fetch('/api/subscriptions/confirm', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ token })
            });
            if (!response.ok) throw new Error(siteStrings.subscriptionFailed);
            const payload: unknown = await response.json();
            if (payload != null && typeof payload === 'object' && typeof (payload as Record<string, unknown>).result === 'string') {
                result = (payload as Record<string, string>).result;
            }
            window.history.replaceState(null, '', `/subscribe/confirm?result=${encodeURIComponent(result)}`);
        } catch {
            subscribeResult.dataset.kind = 'error';
            subscribeResult.textContent = siteStrings.subscriptionFailed;
            return;
        }

        const messages: Record<string, string> = {
            confirmed: siteStrings.subscriptionConfirmed,
            'already-confirmed': siteStrings.subscriptionAlreadyConfirmed,
            expired: siteStrings.subscriptionConfirmationExpired,
            invalid: siteStrings.subscriptionConfirmationInvalid
        };
        subscribeResult.textContent = messages[result] ?? siteStrings.subscriptionConfirmationInvalid;
        subscribeResult.dataset.kind = result === 'confirmed' ? 'success' : 'status';
        return;
    }

    if (normalizedPath === '/subscribe/unsubscribe') {
        unsubscribeView.hidden = false;
        unsubscribeStatus.dataset.kind = 'status';
        unsubscribeStatus.textContent = siteStrings.unsubscribeChecking;
        const token = fragmentParameters.get('token');
        if (token == null || token.length === 0) {
            unsubscribeStatus.dataset.kind = 'error';
            unsubscribeStatus.textContent = siteStrings.unsubscribeInvalid;
            return;
        }

        try {
            const response = await fetch('/api/subscriptions/unsubscribe/validate', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ token })
            });
            if (!response.ok) throw new Error(siteStrings.unsubscribeFailed);
            const payload: unknown = await response.json();
            if (payload == null || typeof payload !== 'object' || (payload as Record<string, unknown>).valid !== true) {
                unsubscribeStatus.dataset.kind = 'error';
                unsubscribeStatus.textContent = siteStrings.unsubscribeInvalid;
                return;
            }

            unsubscribeToken = token;
            unsubscribeStatus.textContent = siteStrings.unsubscribePrompt;
            unsubscribeSubmit.hidden = false;
        } catch {
            unsubscribeStatus.dataset.kind = 'error';
            unsubscribeStatus.textContent = siteStrings.unsubscribeFailed;
        }
        return;
    }

    subscribeFormView.hidden = false;
}

async function submitSubscriptionForm(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (!subscribeForm.reportValidity()) return;

    subscribeSubmit.disabled = true;
    subscribeStatus.dataset.kind = 'status';
    subscribeStatus.textContent = siteStrings.subscriptionSubmitting;
    try {
        const response = await fetch('/api/subscriptions', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                website: subscribeWebsite.value,
                emailAddress: subscribeEmail.value
            })
        });
        if (!response.ok) throw new Error(await readApiError(response, siteStrings.subscriptionFailed));

        subscribeForm.reset();
        subscribeStatus.dataset.kind = 'success';
        subscribeStatus.textContent = siteStrings.subscriptionAccepted;
    } catch (error) {
        subscribeStatus.dataset.kind = 'error';
        subscribeStatus.textContent = error instanceof Error ? error.message : siteStrings.subscriptionFailed;
    } finally {
        subscribeSubmit.disabled = false;
    }
}

async function submitUnsubscribe(): Promise<void> {
    if (unsubscribeToken == null) return;

    unsubscribeSubmit.disabled = true;
    unsubscribeStatus.dataset.kind = 'status';
    unsubscribeStatus.textContent = siteStrings.unsubscribeSubmitting;
    try {
        const response = await fetch('/api/subscriptions/unsubscribe', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ token: unsubscribeToken })
        });
        if (!response.ok) throw new Error(await readApiError(response, siteStrings.unsubscribeFailed));

        unsubscribeToken = null;
        unsubscribeSubmit.hidden = true;
        unsubscribeStatus.dataset.kind = 'success';
        unsubscribeStatus.textContent = siteStrings.unsubscribeComplete;
        window.history.replaceState(null, '', '/subscribe/unsubscribe?result=complete');
    } catch (error) {
        unsubscribeStatus.dataset.kind = 'error';
        unsubscribeStatus.textContent = error instanceof Error ? error.message : siteStrings.unsubscribeFailed;
        unsubscribeSubmit.disabled = false;
    }
}

async function submitContactForm(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (!contactForm.reportValidity()) return;

    contactSend.disabled = true;
    contactStatus.dataset.kind = 'status';
    contactStatus.textContent = siteStrings.contactSending;
    try {
        const response = await fetch('/api/contact', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                website: contactWebsite.value,
                emailAddress: contactEmail.value,
                subject: contactSubject.value,
                body: contactBody.value
            })
        });
        if (!response.ok) throw new Error(await readApiError(response, siteStrings.contactFailed));

        contactForm.reset();
        contactStatus.textContent = '';
        delete contactStatus.dataset.kind;
        await showSiteRoute('home', true);
        if (currentRoute === 'home') showContactToast(siteStrings.contactSent);
    } catch (error) {
        contactStatus.dataset.kind = 'error';
        contactStatus.textContent = error instanceof Error ? error.message : siteStrings.contactFailed;
    } finally {
        contactSend.disabled = false;
    }
}

function requireElement<T extends Element>(id: string, constructor: { new(...args: never[]): T }): T {
    const element = document.getElementById(id);
    if (!(element instanceof constructor)) throw new Error(`Required page element '${id}' was not found.`);
    return element;
}

function calculateCompleteLoopLayout(
    images: readonly PublicPhoto[],
    usableWidth: number,
    targetRowHeight: number,
    minimumItemsPerRow: number,
    maximumItemsPerRow: number
): { images: PublicPhoto[]; layout: ReturnType<typeof calculateLoopingGridLayout>['layout'] } {
    const loopLayout = calculateLoopingGridLayout({
        aspectRatios: images.map(image => image.previewWidth / image.previewHeight),
        usableWidth,
        gap: gapPixels,
        targetRowHeight,
        minimumItemsPerRow,
        maximumItemsPerRow,
        minimumItemSize: 1
    });

    return {
        images: loopLayout.sourceImageIndexes.map(index => images[index]),
        layout: loopLayout.layout
    };
}

function getVisibleTiles(): RenderedTile[] {
    return renderedTiles.filter(tile => isRectangleVisible(tile.element.getBoundingClientRect(), window.innerWidth, window.innerHeight));
}

function getFeatureCandidateTiles(): RenderedTile[] {
    const visibleTiles = getVisibleTiles();
    const rows = new Map<string, RenderedTile[]>();
    for (const tile of visibleTiles) {
        const row = rows.get(tile.rowKey);
        if (row == null) rows.set(tile.rowKey, [tile]);
        else row.push(tile);
    }

    const visibleRows = [...rows.values()];
    const candidateRowIndexes = selectFeatureCandidateRowIndexes(
        visibleRows.map(row => row[0].element.getBoundingClientRect()),
        window.innerHeight);
    return candidateRowIndexes.flatMap(index => visibleRows[index]);
}

function chooseVisibleTile(): RenderedTile | null {
    const eligibleTiles = getFeatureCandidateTiles();
    return featuredImageCycle.selectCandidate(
        eligibleTiles,
        tile => tile.photo.imageKey,
        lastFeaturedImageKey);
}

function waitForFeatureInactivity(request: FeatureRequest, milliseconds: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal.aborted) {
            reject(signal.reason);
            return;
        }

        let delay: PausableDelay | null = null;
        const detach = (): void => {
            delay?.cancel();
            if (request.featureHoldDelay === delay) request.featureHoldDelay = null;
            if (request.resetHoldTimer === resetHoldTimer) request.resetHoldTimer = null;
            signal.removeEventListener('abort', abort);
        };
        const complete = (): void => {
            detach();
            resolve();
        };
        const abort = (): void => {
            detach();
            reject(signal.reason);
        };
        const resetHoldTimer = (): void => {
            delay?.reset();
        };

        delay = new PausableDelay(milliseconds, complete, {
            setTimeout: (callback, delayMilliseconds) => window.setTimeout(callback, delayMilliseconds),
            clearTimeout: timeoutId => window.clearTimeout(timeoutId),
            getTimestamp: () => performance.now()
        });
        request.featureHoldDelay = delay;
        request.resetHoldTimer = resetHoldTimer;
        signal.addEventListener('abort', abort, { once: true });
        delay.start(!isHomepagePlaybackPlaying);
    });
}

function isAutomaticFeatureEligible(): boolean {
    return isHomepagePlaybackPlaying && !document.hidden && config != null
        && featureRequest == null && wallDrag == null && pinchGesture == null
        && !pinchAwaitingRelease && momentumVelocity === 0 && !reducedMotionQuery.matches && currentRoute === 'home';
}

const automaticFeatureTimer = new AutomaticFeatureTimer({
    isEligible: isAutomaticFeatureEligible,
    onElapsed: () => {
        const selectedTile = chooseVisibleTile();
        if (selectedTile == null) {
            scheduleNextAutomaticFeature();
            return;
        }

        void presentFeature(selectedTile, 'automatic', 'auto');
    },
    setTimeout: (callback, delayMilliseconds) => window.setTimeout(callback, delayMilliseconds),
    clearTimeout: timeoutId => window.clearTimeout(timeoutId)
});

function cancelAutomaticFeatureTimer(): void {
    automaticFeatureTimer.cancel();
}

function scheduleNextAutomaticFeature(): void {
    if (config == null) {
        cancelAutomaticFeatureTimer();
        return;
    }

    automaticFeatureTimer.schedule(config.featureIntervalSeconds * 1000);
}

/// Marks the collection navigation as busy while a switch is loading and makes the links inert so a second
/// switch cannot queue behind the first.
function setCollectionNavigationBusy(isBusy: boolean): void {
    if (collectionNavigationBusy === isBusy) return;

    collectionNavigationBusy = isBusy;
    if (isBusy) collectionNavigation.setAttribute('data-busy', 'true');
    else collectionNavigation.removeAttribute('data-busy');
}

function renderHomepagePlaybackControl(): void {
    const presentation = getHomepagePlaybackControlPresentation(isHomepagePlaybackPlaying);
    homepagePlaybackToggle.dataset.playing = String(presentation.isPlaying);
    homepagePlaybackToggle.setAttribute('aria-label', presentation.label);
    homepagePlaybackToggle.setAttribute('aria-pressed', String(!presentation.isPlaying));
    homepagePlaybackToggle.title = presentation.label;
}

function toggleHomepagePlayback(): void {
    isHomepagePlaybackPlaying = !isHomepagePlaybackPlaying;
    lastAnimationTimestamp = 0;
    const request = featureRequest;
    if (isHomepagePlaybackPlaying) {
        request?.featureHoldDelay?.resume();
        if (request?.caption != null && request.captionAnimation == null
            && Number.parseFloat(getComputedStyle(request.caption).opacity) > 0) {
            scheduleFeatureCaptionFadeOut(request);
        }
        scheduleNextAutomaticFeature();
    }
    else {
        automaticScrollVelocity = 0;
        cancelAutomaticFeatureTimer();
        if (request != null && !request.isClosing) {
            request.featureHoldDelay?.pause();
            cancelFeatureCaptionHoldTimer(request);
            request.captionAnimation?.cancel();
            request.captionAnimation = null;
            request.caption?.style.setProperty('opacity', '1');
        }
    }
    renderHomepagePlaybackControl();
}

function showFeatureCloseButton(request: FeatureRequest, fadeIn = true): void {
    if (request.closeButton == null) return;

    request.closeButtonAnimation?.cancel();
    const closeButton = request.closeButton;
    closeButton.disabled = false;
    featureLayer.appendChild(closeButton);
    // The shared button is already visible during slide navigation, so a replacing figure
    // must not replay the fade-in; the button simply stays in place.
    if (!fadeIn) {
        closeButton.style.opacity = '1';
        return;
    }
    closeButton.style.opacity = '';
    const animation = closeButton.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: reducedMotionQuery.matches ? 0 : featuredPhotoCloseFadeMilliseconds,
        easing: 'ease-out',
        fill: 'forwards'
    });
    request.closeButtonAnimation = animation;
    void animation.finished.then(() => {
        if (request.closeButtonAnimation !== animation) return;
        closeButton.style.opacity = '1';
        animation.cancel();
        request.closeButtonAnimation = null;
    }, () => undefined);
}

function showFeatureCaption(request: FeatureRequest): void {
    const caption = request.caption;
    if (caption == null) return;

    cancelFeatureCaptionHoldTimer(request);
    const startingOpacity = getComputedStyle(caption).opacity;
    request.captionAnimation?.cancel();
    const animation = caption.animate([{ opacity: startingOpacity }, { opacity: 1 }], {
        duration: reducedMotionQuery.matches ? 0 : featuredPhotoCaptionFadeMilliseconds,
        easing: 'ease-out',
        fill: 'forwards'
    });
    request.captionAnimation = animation;
    void animation.finished.then(() => {
        if (request.captionAnimation !== animation) return;
        caption.style.opacity = '1';
        animation.cancel();
        request.captionAnimation = null;
        if (isHomepagePlaybackPlaying) scheduleFeatureCaptionFadeOut(request);
    }, () => undefined);
}

function hideFeatureCaption(request: FeatureRequest): Animation | null {
    const caption = request.caption;
    if (caption == null || !caption.isConnected) return null;

    cancelFeatureCaptionHoldTimer(request);
    const startingOpacity = getComputedStyle(caption).opacity;
    request.captionAnimation?.cancel();
    if (Number.parseFloat(startingOpacity) <= 0) {
        caption.style.opacity = '0';
        request.captionAnimation = null;
        return null;
    }
    const animation = caption.animate([{ opacity: startingOpacity }, { opacity: 0 }], {
        duration: reducedMotionQuery.matches ? 0 : featuredPhotoCaptionFadeMilliseconds,
        easing: 'ease-in',
        fill: 'forwards'
    });
    request.captionAnimation = animation;
    void animation.finished.then(() => {
        if (request.captionAnimation !== animation) return;
        caption.style.opacity = '0';
        animation.cancel();
        request.captionAnimation = null;
    }, () => undefined);
    return animation;
}

function cancelFeatureCaptionHoldTimer(request: FeatureRequest): void {
    if (request.captionHoldTimeoutId == null) return;
    window.clearTimeout(request.captionHoldTimeoutId);
    request.captionHoldTimeoutId = null;
}

function scheduleFeatureCaptionFadeOut(request: FeatureRequest): void {
    cancelFeatureCaptionHoldTimer(request);
    if (!isHomepagePlaybackPlaying || config == null || request.caption == null) return;
    // A visitor who opened this photograph chose it, so while prints are for sale its caption and Order print
    // button stay visible until the photograph closes. Automatic showcase photographs still fade their captions.
    if (config.shop.enabled && request.source === 'user') return;

    request.captionHoldTimeoutId = window.setTimeout(() => {
        request.captionHoldTimeoutId = null;
        if (featureRequest === request && !request.isClosing && !request.isReopening) {
            hideFeatureCaption(request);
        }
    }, config.featureCaptionHoldMilliseconds);
}

function finishFeaturePresentation(request: FeatureRequest): void {
    // Every presentation ends here unless it already ended for arrow navigation or checkout. Releases that are not
    // the visitor's doing (a route, collection or layout change) also count as closed.
    endFeatureInsights(request, request.insightsEndReason ?? 'closed');
    const cancellation = new DOMException('The feature presentation was released.', 'AbortError');
    cancelPendingFeaturedImageWork(request, cancellation);
    const closeButton = request.closeButton;
    const gestureState = request.gestureState;
    if (gestureState != null) {
        gestureState.enabled = false;
        gestureState.gesture = null;
        for (const pointerId of gestureState.pointers.keys()) {
            if (featureLayer.hasPointerCapture(pointerId)) featureLayer.releasePointerCapture(pointerId);
        }
        gestureState.pointers.clear();
    }
    cancelFeatureCaptionHoldTimer(request);
    request.captionAnimation?.cancel();
    request.closeButtonAnimation?.cancel();
    for (const animation of request.animations) animation.cancel();
    request.imageLayers.disposeAll();
    request.element?.remove();
    request.element = null;
    request.caption = null;
    request.captionAnimation = null;
    request.captionHoldTimeoutId = null;
    request.closeButton = null;
    request.closeButtonAnimation = null;
    request.animations = [];
    request.closingAnimations = [];
    request.chromeFadeAnimations = [];
    request.gestureState = null;
    request.featureHoldDelay?.cancel();
    request.featureHoldDelay = null;
    request.resetHoldTimer = null;
    request.hasActiveHistoryEntry = false;
    request.isAwaitingHistoryDismissal = false;
    if (featureRequest !== request) return;

    // The shared close button belongs to the active presentation only; slide navigation
    // finishes superseded requests before this check and keeps the button on screen.
    closeButton?.remove();
    featureRequest = null;
    featureLayer.classList.remove('feature-layer-active', 'featured-photo-nav-visible');
    featureLayer.setAttribute('aria-hidden', 'true');
    // Returning to the grid must restore the collection H1 that was hidden while the photograph was open.
    updateDocumentTitle();
    scheduleNextAutomaticFeature();
}

function cancelPendingFeaturedImageWork(request: FeatureRequest, reason: DOMException): void {
    request.controller.abort(reason);
    request.imageLoads.cancelAll(reason);
    const quality = request.gestureState?.quality;
    if (quality == null) return;

    if (quality.preferredStatus === 'loading') {
        quality.preferredStatus = 'idle';
        quality.preferredOperation = null;
        releaseFeaturedImageSource(quality.preferredImage);
    }
    if (quality.originalOperation != null) {
        quality.originalOperation = null;
    }
    const originalImage = quality.originalImage;
    if (originalImage == null) return;

    quality.originalAttempt = null;
    quality.originalRequested = false;
    if (!isFeaturedImageLayerVisible(originalImage)) {
        quality.originalImage = null;
        request.imageLayers.dispose(originalImage);
    }
}

/**
 * Collects the current grid rectangles that display the photograph a feature request shows.
 *
 * The request's source tile element is deliberately not trusted: the looping grid wraps the
 * scroll offset by one full sequence height, and tile recycling can repurpose the element for
 * another photograph without disconnecting it, so the close animation must re-derive the
 * photograph's position from the rendered grid by image key.
 */
function getFeatureReturnCandidateRects(request: FeatureRequest): Rectangle[] {
    return renderedTiles
        .filter(tile => tile.photo.imageKey === request.imageKey && tile.element.isConnected)
        .map(tile => tile.element.getBoundingClientRect());
}

function getFeatureReturnRectangle(request: FeatureRequest, duration: number): Rectangle | null {
    requestFeatureGridPauseIfNeeded(request);
    const rectangle = selectFeatureReturnRectangle(
        getFeatureReturnCandidateRects(request),
        window.innerWidth,
        window.innerHeight);
    if (rectangle == null) return null;

    const wallTravel = config != null && !document.hidden && !reducedMotionQuery.matches
        ? calculateVelocityTransitionDistance(
            automaticScrollVelocity,
            request.pauseGridScrolling ? 0 : config.scrollPixelsPerSecond,
            config.scrollPixelsPerSecond / automaticScrollTransitionSeconds,
            duration / 1000)
        : 0;
    return {
        left: rectangle.left,
        right: rectangle.right,
        top: rectangle.top - wallTravel,
        bottom: rectangle.bottom - wallTravel,
        width: rectangle.width,
        height: rectangle.height
    };
}

function requestFeatureGridPauseIfNeeded(request: FeatureRequest): void {
    if (request.isClosing || request.pauseGridScrolling || config == null) return;

    // The pause decision uses the closest visible copy of the photograph rather than the
    // request's source tile: the looping grid can move the original element a full sequence
    // away, and tile recycling can repurpose it for another photograph entirely.
    const rectangle = selectFeatureReturnRectangle(
        getFeatureReturnCandidateRects(request),
        window.innerWidth,
        window.innerHeight);
    const automaticScrollAcceleration = config.scrollPixelsPerSecond / automaticScrollTransitionSeconds;
    const stoppingDistance = calculateStoppingDistance(automaticScrollVelocity, automaticScrollAcceleration);
    if (rectangle != null && rectangle.top <= stoppingDistance) request.pauseGridScrolling = true;
}

/**
 * Fades the viewer chrome (the close button and the previous/next navigation buttons) out in
 * step with the photograph's close animation. The buttons stay interactive while fading so a
 * click during the close still reverses the animation and navigates, and the fade animations
 * are tracked on the request so the reversal can play them backward.
 *
 * @param request - The presentation whose chrome should fade out.
 * @param duration - Fade duration in milliseconds, matching the photograph's close animation.
 */
function fadeOutFeatureChrome(request: FeatureRequest, duration: number): void {
    const chromeAnimations: Animation[] = [];
    const closeButton = request.closeButton;
    if (closeButton != null && closeButton.isConnected) {
        request.closeButtonAnimation?.cancel();
        closeButton.disabled = true;
        const startingOpacity = getComputedStyle(closeButton).opacity;
        const animation = closeButton.animate([{ opacity: startingOpacity }, { opacity: 0 }], {
            duration: reducedMotionQuery.matches ? 0 : duration,
            easing: 'ease-in',
            fill: 'forwards'
        });
        request.closeButtonAnimation = animation;
        chromeAnimations.push(animation);
        void animation.finished.then(() => {
            if (request.closeButtonAnimation !== animation) return;
            closeButton.style.opacity = '0';
            animation.cancel();
            request.closeButtonAnimation = null;
        }, () => undefined);
    }

    // The shared navigation buttons belong to the layer rather than the presentation, so their
    // fade animations are only tracked on the request for reversal and finish-time cleanup.
    for (const button of [featurePreviousNavButton, featureNextNavButton]) {
        if (button == null) continue;
        const startingOpacity = getComputedStyle(button).opacity;
        chromeAnimations.push(button.animate([{ opacity: startingOpacity }, { opacity: 0 }], {
            duration: reducedMotionQuery.matches ? 0 : duration,
            easing: 'ease-in',
            fill: 'forwards'
        }));
    }

    request.chromeFadeAnimations = chromeAnimations;
    request.animations.push(...chromeAnimations);
}

async function collapseFeature(request: FeatureRequest, duration: number): Promise<void> {
    if (request.isClosing) return;
    const transitionGeneration = ++request.transitionGeneration;
    request.isClosing = true;
    request.isReopening = false;
    request.pauseGridScrolling = false;
    if (request.gestureState != null) request.gestureState.enabled = false;
    request.element?.classList.remove('featured-photo-nav-visible');
    // The layer stays interactive for the whole close animation so clicks cannot fall
    // through to the photo wall underneath; finishFeaturePresentation releases it when the
    // presentation actually ends.
    const cancellation = new DOMException('The feature presentation was dismissed.', 'AbortError');
    cancelPendingFeaturedImageWork(request, cancellation);

    const captionAnimation = hideFeatureCaption(request);
    if (captionAnimation != null) {
        request.closingAnimations = [captionAnimation];
        try {
            await captionAnimation.finished;
        } catch (error) {
            if (!(error instanceof DOMException && error.name === 'AbortError')) throw error;
        }
        if (request.transitionGeneration !== transitionGeneration) return;
        request.caption?.style.setProperty('opacity', '0');
        captionAnimation.cancel();
        if (request.captionAnimation === captionAnimation) request.captionAnimation = null;
        request.closingAnimations = [];
    }

    // The close and previous/next buttons fade out together with the photograph; they remain
    // interactive while fading so a click still reverses the close and navigates.
    fadeOutFeatureChrome(request, duration);

    const feature = request.element;
    if (feature != null) {
        const targetRectangle = getFeatureReturnRectangle(request, duration);
        const closeAnimations: Animation[] = [];
        const featureStyle = getComputedStyle(feature);
        const currentOpacity = featureStyle.opacity;
        const currentBackgroundColor = featureStyle.backgroundColor;
        const contentStyle = request.gestureState == null ? null : getComputedStyle(request.gestureState.content);
        const currentTransform = contentStyle?.transform ?? 'none';
        const currentScale = currentTransform === 'none'
            ? 1
            : new DOMMatrixReadOnly(currentTransform).a;
        const currentLocalRadius = request.gestureState == null
            ? 0
            : Number.parseFloat(getComputedStyle(request.gestureState.frame).borderTopLeftRadius) || 0;
        for (const animation of request.closingAnimations) animation.cancel();
        request.closingAnimations = [];
        if (targetRectangle == null || request.gestureState == null) {
            closeAnimations.push(feature.animate([{ opacity: currentOpacity }, { opacity: 0 }], {
                duration,
                easing: 'ease-in',
                fill: 'forwards'
            }));
        } else {
            closeAnimations.push(feature.animate([
                { backgroundColor: currentBackgroundColor },
                { backgroundColor: 'rgb(5 5 5 / 0%)' }
            ], {
                duration,
                easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
                fill: 'forwards'
            }));
            const targetTransform = calculateFeaturedImageFrameTransform(
                request.gestureState.imageBounds,
                targetRectangle);
            closeAnimations.push(request.gestureState.content.animate([
                { transform: currentTransform },
                { transform: formatViewportTransform(targetTransform) }
            ], {
                duration,
                easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
                fill: 'forwards'
            }));
            closeAnimations.push(request.gestureState.frame.animate(
                createCornerRadiusKeyframes(
                    currentScale,
                    targetTransform.scale,
                    currentLocalRadius * currentScale,
                    request.gestureState.cornerRadius), {
                    duration,
                    easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
                    fill: 'forwards'
                }));
        }

        request.animations.push(...closeAnimations);
        request.closingAnimations = closeAnimations;
        try {
            await Promise.all(closeAnimations.map(animation => animation.finished));
        } catch (error) {
            if (!(error instanceof DOMException && error.name === 'AbortError')) throw error;
        }
    }

    if (request.transitionGeneration !== transitionGeneration) return;
    finishFeaturePresentation(request);
}

/**
 * Reverses an in-progress close animation so the featured photograph returns to its fully
 * open state. Bumping the transition generation aborts the pending collapse, the closing
 * animations are played backwards, and the viewer chrome (close button, caption, gestures)
 * is restored. Reopen flows for history restoration and for navigation during the close
 * animation both reuse this helper.
 *
 * @param request - The presentation whose close animation should be reversed.
 * @returns True when the photograph was fully re-opened and is still the active presentation.
 */
async function reverseClosingFeature(request: FeatureRequest): Promise<boolean> {
    const transitionGeneration = ++request.transitionGeneration;
    // The photograph collapse and the button fades play backward together, so the viewer
    // chrome reappears exactly as smoothly as the photograph itself.
    const reversedAnimations = [...request.closingAnimations, ...request.chromeFadeAnimations];
    let signal: AbortSignal | null = null;
    request.isClosing = false;
    request.isReopening = true;
    // The same presentation continues, so a timed-out close that is reversed no longer explains its end.
    request.insightsEndReason = null;
    // The layer is never deactivated while a presentation closes, but reasserting the class
    // keeps slide navigation from a closing presentation robust.
    featureLayer.classList.add('feature-layer-active');

    try {
        if (reversedAnimations.length > 0) {
            for (const animation of reversedAnimations) animation.reverse();
            await Promise.all(reversedAnimations.map(animation => animation.finished));
            if (request.transitionGeneration !== transitionGeneration || featureRequest !== request) return false;

            for (const animation of reversedAnimations) animation.cancel();
            request.closingAnimations = [];
            request.chromeFadeAnimations = [];
            request.closeButtonAnimation = null;
            request.animations = request.animations.filter(animation => !reversedAnimations.includes(animation));
        }
        if (request.controller.signal.aborted) request.controller = new AbortController();
        signal = request.controller.signal;
        request.isReopening = false;
        if (request.gestureState != null) {
            request.gestureState.enabled = true;
            void ensurePreferredFeaturedImage(request, request.gestureState, signal);
            requestFeaturedImageOriginalIfNeeded(request.gestureState);
        }
        showFeatureCloseButton(request, false);
        request.element?.classList.add('featured-photo-nav-visible');
        showFeatureCaption(request);
        return true;
    } catch (error) {
        if (!isFeaturedImageLoadCancellation(error) && signal?.aborted !== true) {
            console.warn('Featured photograph could not be reopened.', error);
        }
        return false;
    }
}

async function reopenClosingFeature(request: FeatureRequest): Promise<void> {
    if (!request.isClosing
        || (request.closingAnimations.length === 0 && request.chromeFadeAnimations.length === 0)
        || config == null) return;
    if (!(await reverseClosingFeature(request))) return;

    const signal = request.controller.signal;
    try {
        await waitForFeatureInactivity(request, userFeatureHoldMilliseconds, signal);
        request.insightsEndReason ??= 'timed-out';
        requestFeatureDismissal(request);
    } catch (error) {
        if (!isFeaturedImageLoadCancellation(error) && signal.aborted !== true) {
            console.warn('Featured photograph could not be reopened.', error);
        }
    } finally {
        if (request.controller.signal === signal
            && featureRequest === request
            && !request.isClosing
            && !request.isReopening
            && !request.isAwaitingHistoryDismissal) {
            if (request.hasActiveHistoryEntry) requestFeatureDismissal(request);
            else finishFeaturePresentation(request);
        }
    }
}

function cancelFeaturePresentationImmediately(): void {
    const request = featureRequest;
    if (request == null) return;
    finishFeaturePresentation(request);
}

function requestFeatureDismissal(request: FeatureRequest): void {
    if (featureRequest !== request || request.isClosing || request.isAwaitingHistoryDismissal) return;

    if (request.hasActiveHistoryEntry) {
        request.isAwaitingHistoryDismissal = true;
        window.history.back();
        return;
    }

    if (config == null) finishFeaturePresentation(request);
    else void collapseFeature(request, reducedMotionQuery.matches ? 0 : config.featureFadeMilliseconds);
}

function dismissFeaturePresentation(): void {
    const request = featureRequest;
    if (request != null) requestFeatureDismissal(request);
}

/// Closes the featured photograph on Escape, matching the close button.
/// @param event Keyboard event raised by the document-level keydown listener.
/// @returns Nothing; the presentation is dismissed when a photograph is on screen.
function handleFeatureKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    const request = featureRequest;
    // Skip presentations that are already collapsing, reopening, or waiting for the history entry
    // to pop; dismissal is a no-op there and Escape must not swallow another handler's key press.
    if (request == null || request.isClosing || request.isReopening || request.isAwaitingHistoryDismissal) return;
    event.preventDefault();
    requestFeatureDismissal(request);
}

function getTouchCenter(first: TouchPoint, second: TouchPoint): TouchPoint {
    return { x: (first.x + second.x) * 0.5, y: (first.y + second.y) * 0.5 };
}

function formatViewportTransform(transform: ViewportTransform): string {
    return `translate(${transform.panX}px, ${transform.panY}px) scale(${transform.scale})`;
}

function applyFeaturedImageTransform(state: FeaturedImageGestureState): void {
    state.content.style.transform = formatViewportTransform(state.transform);
    requestFeaturedImageOriginalIfNeeded(state);
}

function createFeaturedImage(request: FeatureRequest, className: string, altText: string, source?: string): HTMLImageElement {
    const image = request.imageLayers.own(new Image());
    image.alt = altText;
    image.decoding = 'async';
    // The featured overlay pans with the mouse, so a press-and-drag on a layer must never be
    // promoted to a native image drag; the CSS hint alone is not enough and Firefox ignores it.
    image.draggable = false;
    image.className = className;
    Object.assign(image.style, { left: '0', top: '0', width: '100%', height: '100%' });
    if (source != null) image.src = source;
    return image;
}

function isFeaturedImageLayerVisible(image: HTMLImageElement): boolean {
    return image.isConnected && Number.parseFloat(getComputedStyle(image).opacity) > 0;
}

async function revealFeaturedImage(
    request: FeatureRequest,
    image: HTMLImageElement,
    signal: AbortSignal
): Promise<void> {
    const startingOpacity = getComputedStyle(image).opacity;
    const revealAnimation = image.animate([{ opacity: startingOpacity }, { opacity: 1 }], {
        duration: reducedMotionQuery.matches ? 0 : 220,
        easing: 'ease-out',
        fill: 'forwards'
    });
    request.animations.push(revealAnimation);
    const abortWait = new Promise<never>((_, reject) => {
        const onAbort = (): void => {
            image.style.opacity = getComputedStyle(image).opacity;
            revealAnimation.cancel();
            reject(signal.reason);
        };
        signal.addEventListener('abort', onAbort, { once: true });
        const removeAbortListener = (): void => signal.removeEventListener('abort', onAbort);
        void revealAnimation.finished.then(removeAbortListener, removeAbortListener);
        if (signal.aborted) onAbort();
    });
    await Promise.race([revealAnimation.finished, abortWait]);
}

function isFeatureLifetimeCurrent(request: FeatureRequest, signal: AbortSignal): boolean {
    return featureRequest === request
        && request.controller.signal === signal
        && !signal.aborted
        && !request.isClosing;
}

async function ensurePreferredFeaturedImage(
    request: FeatureRequest,
    state: FeaturedImageGestureState,
    signal: AbortSignal
): Promise<void> {
    const quality = state.quality;
    if (quality.preferredStatus === 'displayed' || quality.preferredStatus === 'failed') return;

    if (quality.preferredStatus === 'idle') {
        quality.preferredStatus = 'loading';
        let source = quality.preferredImageUrl;
        let operation = request.imageLoads.start(quality.preferredImage, source, signal);
        quality.preferredOperation = operation;
        try {
            await operation.result;
        } catch (error) {
            if (quality.preferredOperation !== operation) return;
            if (isFeaturedImageLoadCancellation(error)) {
                quality.preferredStatus = 'idle';
                return;
            }
            if (!isFeatureLifetimeCurrent(request, signal)) return;
            if (source === quality.fullResolutionImageUrl) {
                quality.preferredStatus = 'failed';
                console.warn('The featured photograph could not be loaded.', error);
                return;
            }

            console.warn('The display-resolution featured photograph could not be loaded; falling back to the original.', error);
            source = quality.fullResolutionImageUrl;
            operation = request.imageLoads.start(quality.preferredImage, source, signal);
            quality.preferredOperation = operation;
            try {
                await operation.result;
            } catch (fallbackError) {
                if (quality.preferredOperation !== operation) return;
                if (isFeaturedImageLoadCancellation(fallbackError)) {
                    quality.preferredStatus = 'idle';
                    return;
                }
                quality.preferredStatus = 'failed';
                if (isFeatureLifetimeCurrent(request, signal)) {
                    console.warn('The original featured photograph could not be loaded.', fallbackError);
                }
                return;
            }
        } finally {
            if (quality.preferredOperation === operation) quality.preferredOperation = null;
        }

        if (!isFeatureLifetimeCurrent(request, signal)) {
            if (quality.preferredStatus === 'loading') quality.preferredStatus = 'idle';
            return;
        }
        quality.preferredImageIsOriginal = source === quality.fullResolutionImageUrl;
        quality.preferredStatus = 'ready';
    }

    if (quality.preferredStatus !== 'ready' || !isFeatureLifetimeCurrent(request, signal)) return;
    quality.preferredRevealSignal = signal;
    try {
        await revealFeaturedImage(request, quality.preferredImage, signal);
    } catch (error) {
        if (!isFeaturedImageLoadCancellation(error)) throw error;
        return;
    } finally {
        if (quality.preferredRevealSignal === signal) quality.preferredRevealSignal = null;
    }
    if (quality.preferredStatus !== 'ready'
        || quality.preferredRevealSignal != null
        || !isFeatureLifetimeCurrent(request, signal)) return;

    quality.activeImage = quality.preferredImage;
    quality.availableWidth = quality.preferredImage.naturalWidth;
    quality.availableHeight = quality.preferredImage.naturalHeight;
    quality.isOriginal = quality.preferredImageIsOriginal;
    quality.originalRequested = quality.preferredImageIsOriginal;
    quality.preferredStatus = 'displayed';
    requestFeaturedImageOriginalIfNeeded(state);
}

function requestFeaturedImageOriginalIfNeeded(state: FeaturedImageGestureState): void {
    const request = featureRequest;
    const quality = state.quality;
    if (!shouldRequestFeaturedImageOriginal({
        isActive: request?.gestureState === state && state.enabled,
        isClosing: request?.isClosing ?? true,
        isOriginal: quality.isOriginal,
        originalRequested: quality.originalRequested,
        demand: {
            renderedWidth: state.imageBounds.width,
            renderedHeight: state.imageBounds.height,
            scale: state.transform.scale,
            devicePixelRatio: window.devicePixelRatio || 1,
            availableWidth: quality.availableWidth,
            availableHeight: quality.availableHeight
        }
    }) || request == null) return;

    quality.originalRequested = true;
    void (async () => {
        const signal = request.controller.signal;
        const retainedOriginalImage = quality.originalImage;
        const originalImage = retainedOriginalImage
            ?? createFeaturedImage(request, 'featured-photo-image featured-photo-full', quality.preferredImage.alt);
        const attempt = {};
        quality.originalImage = originalImage;
        quality.originalAttempt = attempt;
        if (retainedOriginalImage == null) quality.frame.appendChild(originalImage);
        let operation: FeaturedImageLoadOperation | null = null;
        try {
            if (retainedOriginalImage == null) {
                operation = request.imageLoads.start(originalImage, quality.fullResolutionImageUrl, signal);
                quality.originalOperation = operation;
                await operation.result;
            }
            if (quality.originalAttempt !== attempt
                || quality.originalImage !== originalImage
                || request.gestureState !== state
                || !isFeatureLifetimeCurrent(request, signal)) {
                if (quality.originalImage !== originalImage) request.imageLayers.dispose(originalImage);
                return;
            }

            await revealFeaturedImage(request, originalImage, signal);
            if (quality.originalAttempt !== attempt
                || quality.originalImage !== originalImage
                || request.gestureState !== state
                || !isFeatureLifetimeCurrent(request, signal)) {
                if (quality.originalImage !== originalImage) request.imageLayers.dispose(originalImage);
                return;
            }

            const previousImage = quality.activeImage;
            quality.activeImage = originalImage;
            quality.availableWidth = originalImage.naturalWidth;
            quality.availableHeight = originalImage.naturalHeight;
            quality.isOriginal = true;
            quality.originalImage = null;
            quality.originalAttempt = null;
            quality.originalOperation = null;
            request.imageLayers.dispose(previousImage);
        } catch (error) {
            if (quality.originalAttempt !== attempt || quality.originalImage !== originalImage) {
                if (quality.originalImage !== originalImage) request.imageLayers.dispose(originalImage);
                return;
            }

            quality.originalAttempt = null;
            quality.originalOperation = null;
            if (isFeaturedImageLoadCancellation(error)) {
                quality.originalRequested = false;
                if (request.isClosing && isFeaturedImageLayerVisible(originalImage)) return;
            } else if (isFeatureLifetimeCurrent(request, signal)) {
                console.warn('The original featured photograph could not be loaded.', error);
            }
            quality.originalImage = null;
            request.imageLayers.dispose(originalImage);
        } finally {
            if (quality.originalOperation === operation) quality.originalOperation = null;
        }
    })();
}

function noteFeaturedImageInteraction(state: FeaturedImageGestureState): void {
    const request = featureRequest;
    if (request?.gestureState === state && state.enabled) request.resetHoldTimer?.();
}

function zoomFeaturedImageWithWheel(event: WheelEvent): void {
    const state = featureRequest?.gestureState;
    if (state == null || !state.enabled || event.deltaY === 0) return;

    noteFeaturedImageInteraction(state);
    markFeaturedImageInsight(state, 'zoomed');
    const deltaY = event.deltaY * (event.deltaMode === WheelEvent.DOM_DELTA_LINE
        ? 16
        : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
            ? window.innerHeight
            : 1);
    state.transform = calculateFeaturedImageWheelTransform({
        startingTransform: state.transform,
        focalPointX: event.clientX,
        focalPointY: event.clientY,
        scaleFactor: Math.exp(-deltaY * 0.002),
        minimumScale: state.minimumScale,
        maximumScale: maximumFeaturedImageScale,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        imageBounds: state.imageBounds
    });
    applyFeaturedImageTransform(state);
    event.preventDefault();
}

function isFeaturedImagePanPointer(event: PointerEvent): boolean {
    return event.pointerType === 'touch'
        || (event.pointerType === 'mouse' && event.isPrimary && event.button === 0);
}

function beginFeaturedImageGesture(event: PointerEvent): void {
    const state = featureRequest?.gestureState;
    if (!isFeaturedImagePanPointer(event) || state == null || !state.enabled) return;

    noteFeaturedImageInteraction(state);
    // Pressing on the photograph counts as an interaction; the caption and buttons stop their own pointerdown.
    markFeaturedImageInsight(state, 'interacted');
    state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    featureLayer.setPointerCapture(event.pointerId);
    if (state.pointers.size === 1 && state.transform.scale > state.minimumScale) {
        state.gesture = {
            kind: 'pan',
            pointerId: event.pointerId,
            startingPoint: { x: event.clientX, y: event.clientY },
            startingTransform: { ...state.transform },
            hasMoved: false
        };
    } else if (state.pointers.size === 1) {
        state.gesture = {
            kind: 'swipe',
            pointerId: event.pointerId,
            startingPoint: { x: event.clientX, y: event.clientY },
            startingTimestamp: event.timeStamp,
            isHorizontal: false
        };
    } else if (state.pointers.size === 2) {
        const pointerEntries = [...state.pointers.entries()];
        const first = pointerEntries[0];
        const second = pointerEntries[1];
        const startingDistance = getTouchDistance(first[1], second[1]);
        if (startingDistance > 0) {
            state.gesture = {
                kind: 'pinch',
                pointerIds: [first[0], second[0]],
                startingCenter: getTouchCenter(first[1], second[1]),
                startingDistance,
                startingTransform: { ...state.transform }
            };
            state.suppressDismiss = true;
        }
    }
    if (event.pointerType === 'touch') event.preventDefault();
}

function moveFeaturedImageGesture(event: PointerEvent): void {
    const state = featureRequest?.gestureState;
    if (state == null || !state.enabled) return;
    const previousPoint = state.pointers.get(event.pointerId);
    if (previousPoint == null) return;
    if (event.pointerType === 'mouse' && (event.buttons & 1) === 0) {
        endFeaturedImageGesture(event);
        return;
    }
    if (previousPoint.x === event.clientX && previousPoint.y === event.clientY) {
        if (event.pointerType === 'touch') event.preventDefault();
        return;
    }

    noteFeaturedImageInteraction(state);
    state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const gesture = state.gesture;
    if (gesture?.kind === 'pinch' && gesture.pointerIds.includes(event.pointerId)) {
        const first = state.pointers.get(gesture.pointerIds[0]);
        const second = state.pointers.get(gesture.pointerIds[1]);
        if (first != null && second != null) {
            const currentDistance = getTouchDistance(first, second);
            if (currentDistance > 0) {
                const center = getTouchCenter(first, second);
                state.transform = calculateFeaturedImagePinchTransform({
                    startingTransform: gesture.startingTransform,
                    startingCenterX: gesture.startingCenter.x,
                    startingCenterY: gesture.startingCenter.y,
                    currentCenterX: center.x,
                    currentCenterY: center.y,
                    startingDistance: gesture.startingDistance,
                    currentDistance,
                    minimumScale: state.minimumScale,
                    maximumScale: maximumFeaturedImageScale,
                    viewportWidth: window.innerWidth,
                    viewportHeight: window.innerHeight,
                    imageBounds: state.imageBounds
                });
                applyFeaturedImageTransform(state);
                markFeaturedImageInsight(state, 'zoomed');
            }
        }
    } else if (gesture?.kind === 'swipe' && gesture.pointerId === event.pointerId) {
        const deltaX = event.clientX - gesture.startingPoint.x;
        const deltaY = event.clientY - gesture.startingPoint.y;
        if (Math.abs(deltaX) >= dragThresholdPixels && Math.abs(deltaX) > Math.abs(deltaY)) {
            gesture.isHorizontal = true;
        }
    } else if (gesture?.kind === 'pan' && gesture.pointerId === event.pointerId) {
        const distance = Math.hypot(event.clientX - gesture.startingPoint.x, event.clientY - gesture.startingPoint.y);
        if (gesture.hasMoved || distance >= dragThresholdPixels) {
            gesture.hasMoved = true;
            state.suppressDismiss = true;
            state.transform = calculateFeaturedImagePanTransform({
                startingTransform: gesture.startingTransform,
                startingX: gesture.startingPoint.x,
                startingY: gesture.startingPoint.y,
                currentX: event.clientX,
                currentY: event.clientY,
                viewportWidth: window.innerWidth,
                viewportHeight: window.innerHeight,
                imageBounds: state.imageBounds
            });
            applyFeaturedImageTransform(state);
            // Panning is only possible on a zoomed photograph, so it counts as zooming.
            markFeaturedImageInsight(state, 'zoomed');
        }
    }
    if (event.pointerType === 'touch') event.preventDefault();
}

function endFeaturedImageGesture(event: PointerEvent): void {
    const state = featureRequest?.gestureState;
    if (state == null || !state.pointers.has(event.pointerId)) return;

    noteFeaturedImageInteraction(state);
    const endingGesture = state.gesture;
    state.pointers.delete(event.pointerId);
    if (featureLayer.hasPointerCapture(event.pointerId)) featureLayer.releasePointerCapture(event.pointerId);
    if (endingGesture?.kind === 'pinch' && endingGesture.pointerIds.includes(event.pointerId)) {
        const remainingPointer = [...state.pointers.entries()][0];
        state.gesture = remainingPointer == null || state.transform.scale <= state.minimumScale
            ? null
            : {
                kind: 'pan',
                pointerId: remainingPointer[0],
                startingPoint: { ...remainingPointer[1] },
                startingTransform: { ...state.transform },
                hasMoved: false
            };
    } else if (endingGesture?.kind === 'swipe' && endingGesture.pointerId === event.pointerId) {
        state.gesture = null;
        if (endingGesture.isHorizontal) {
            const deltaX = event.clientX - endingGesture.startingPoint.x;
            const deltaY = event.clientY - endingGesture.startingPoint.y;
            const duration = event.timeStamp - endingGesture.startingTimestamp;
            if (Math.abs(deltaX) >= featuredSwipeDistancePixels
                && Math.abs(deltaX) > Math.abs(deltaY)
                && duration <= featuredSwipeDurationMilliseconds) {
                state.suppressDismiss = true;
                suppressNextFeatureDismiss = true;
                window.setTimeout(() => {
                    suppressNextFeatureDismiss = false;
                }, featuredSwipeClickSuppressionMilliseconds);
                navigateFeaturedImage(deltaX < 0 ? 1 : -1);
            }
        }
    } else if (endingGesture?.kind === 'pan' && endingGesture.pointerId === event.pointerId) {
        state.gesture = null;
    }
    if (state.pointers.size === 0) {
        window.setTimeout(() => {
            if (featureRequest?.gestureState === state && state.pointers.size === 0) state.suppressDismiss = false;
        }, 400);
    }
    if (event.pointerType === 'touch') event.preventDefault();
}

async function presentFeature(
    selectedTile: RenderedTile,
    source: FeatureSource,
    trigger: PhotoPresentationTrigger,
    addHistoryEntry = true,
    slideDirection: -1 | 1 | null = null
): Promise<void> {
    if (config == null || (source === 'automatic' && !isAutomaticFeatureEligible())) return;
    cancelAutomaticFeatureTimer();
    const previousRequest = featureRequest;
    // Slide navigation keeps the previous presentation on screen so its opaque backdrop remains
    // visible and its photograph can slide out while the next one slides in; superseded
    // presentations are released once the slide completes. The expanding presentation keeps the
    // original behavior and cancels the previous one up front.
    if (slideDirection == null) cancelFeaturePresentationImmediately();

    const startingRectangle = selectedTile.standalone === true ? null : selectedTile.element.getBoundingClientRect();
    const request: FeatureRequest = {
        source,
        imageKey: selectedTile.photo.imageKey,
        sourceTile: selectedTile,
        hasActiveHistoryEntry: !addHistoryEntry,
        isAwaitingHistoryDismissal: false,
        pauseGridScrolling: false,
        controller: new AbortController(),
        imageLayers: new FeaturedImageLayerOwner(),
        imageLoads: new FeaturedImageLoadOwner(),
        element: null,
        caption: null,
        captionAnimation: null,
        captionHoldTimeoutId: null,
        closeButton: null,
        closeButtonAnimation: null,
        animations: [],
        closingAnimations: [],
        chromeFadeAnimations: [],
        transitionGeneration: 0,
        isClosing: false,
        isReopening: false,
        gestureState: null,
        featureHoldDelay: null,
        resetHoldTimer: null,
        insights: null,
        insightsEndReason: null
    };
    featureRequest = request;
    const signal = request.controller.signal;

    try {
        if (source === 'automatic' && !getFeatureCandidateTiles().includes(selectedTile)) return;
        // Only titled photographs have public photo pages, and the server accepts records only for those.
        const insightsTitle = selectedTile.photo.title;
        if (insightsTitle != null && insightsTitle.trim().length > 0) {
            request.insights = photoInsights.begin(
                request.imageKey,
                source === 'automatic' ? 'auto' : 'user',
                source === 'automatic' ? 'auto' : trigger);
        }

        const placement = calculateImagePlacement({
            imageWidth: selectedTile.photo.fullResolutionWidth,
            imageHeight: selectedTile.photo.fullResolutionHeight,
            frameWidth: window.innerWidth,
            frameHeight: window.innerHeight,
            poiX: selectedTile.photo.poiX,
            poiY: selectedTile.photo.poiY,
            zoomMode: config.zoomMode
        });
        const tileImage = selectedTile.element.querySelector('img');
        const thumbnailImage = createFeaturedImage(
            request,
            'featured-photo-image featured-photo-thumbnail',
            '',
            tileImage?.currentSrc || selectedTile.photo.thumbnailImageUrl);
        const imageBounds = {
            left: placement.panX,
            top: placement.panY,
            width: placement.renderedWidth,
            height: placement.renderedHeight
        };
        const preferredImageUrl = selectedTile.photo.displayResolutionImageUrl ?? selectedTile.photo.fullResolutionImageUrl;
        const preferredImage = createFeaturedImage(
            request,
            'featured-photo-image featured-photo-full',
            getPhotoAltText(selectedTile.photo));

        const feature = document.createElement('figure');
        feature.className = 'featured-photo';
        feature.style.margin = '0';
        // Slide navigation must keep the new figure transparent while the outgoing photograph
        // is still visible underneath; the stylesheet background is opaque and would otherwise
        // hide the outgoing photograph for the entire slide. The opaque backdrop is applied
        // after the slide completes.
        if (slideDirection != null) feature.style.backgroundColor = 'transparent';
        // The close button is one shared element that lives directly in the feature layer, so it
        // stays visible across slide navigation exactly like the playback toggle instead of
        // disappearing with each outgoing figure.
        let closeButton = featureCloseButton;
        if (closeButton == null) {
            closeButton = document.createElement('button');
            closeButton.type = 'button';
            closeButton.className = 'content-panel-close featured-photo-close';
            closeButton.setAttribute('aria-label', siteStrings.featuredPhotoCloseLabel);
            const closeIcon = document.createElement('span');
            closeIcon.setAttribute('aria-hidden', 'true');
            closeIcon.textContent = '�';
            closeButton.appendChild(closeIcon);
            closeButton.addEventListener('pointerdown', event => event.stopPropagation());
            closeButton.addEventListener('click', event => {
                event.stopPropagation();
                dismissFeaturePresentation();
            });
            featureLayer.appendChild(closeButton);
            featureCloseButton = closeButton;
        }
        // The previous/next navigation buttons are shared layer-level elements, so they stay
        // visible across slide navigation just like the close button, instead of disappearing
        // with each outgoing figure.
        let previousNavButton = featurePreviousNavButton;
        if (previousNavButton == null) {
            previousNavButton = createFeaturedPhotoNavButton(
                -1,
                'featured-photo-nav-previous',
                siteStrings.featuredPhotoPreviousLabel);
            featureLayer.appendChild(previousNavButton);
            featurePreviousNavButton = previousNavButton;
        }
        let nextNavButton = featureNextNavButton;
        if (nextNavButton == null) {
            nextNavButton = createFeaturedPhotoNavButton(
                1,
                'featured-photo-nav-next',
                siteStrings.featuredPhotoNextLabel);
            featureLayer.appendChild(nextNavButton);
            featureNextNavButton = nextNavButton;
        }
        // A standalone photograph is not part of the wall's sequence, so it has no previous or next photograph.
        featureLayer.classList.toggle('featured-photo-nav-enabled', !mobilePhone && selectedTile.standalone !== true);
        const content = document.createElement('div');
        content.className = 'featured-photo-content';
        const frame = document.createElement('div');
        frame.className = 'featured-photo-frame';
        Object.assign(frame.style, {
            left: `${imageBounds.left}px`,
            top: `${imageBounds.top}px`,
            width: `${imageBounds.width}px`,
            height: `${imageBounds.height}px`
        });
        frame.append(thumbnailImage, preferredImage);
        content.appendChild(frame);
        const photoTitle = selectedTile.photo.title;
        const photoDescription = selectedTile.photo.description;
        const caption = document.createElement('figcaption');
        caption.className = 'featured-photo-caption';
        const captionPosition = selectFeatureCaptionPosition(
            imageBounds.left + imageBounds.width * selectedTile.photo.poiX,
            imageBounds.top + imageBounds.height * selectedTile.photo.poiY,
            window.innerWidth,
            window.innerHeight);
        caption.classList.add(
            `featured-photo-caption-${captionPosition.horizontal}`,
            `featured-photo-caption-${captionPosition.vertical}`);
        caption.addEventListener('pointerdown', event => event.stopPropagation());
        caption.addEventListener('click', event => event.stopPropagation());
        if (photoTitle != null && photoTitle.trim().length > 0) {
            // The photo name is the page's subject, so it is the sole H1 while the photograph is open.
            const title = document.createElement('h1');
            title.className = 'featured-photo-title';
            title.textContent = photoTitle;
            caption.appendChild(title);
        }
        if (photoDescription != null && photoDescription.trim().length > 0) {
            const description = document.createElement('p');
            description.className = 'featured-photo-description';
            description.textContent = photoDescription;
            caption.appendChild(description);
        }
        if (photoTitle != null && photoTitle.trim().length > 0) {
            const actions = document.createElement('div');
            actions.className = 'featured-photo-actions';
            const copyButton = document.createElement('button');
            copyButton.type = 'button';
            copyButton.className = 'featured-photo-copy-link';
            copyButton.textContent = siteStrings.featuredPhotoCopyLink;
            const copyStatus = document.createElement('span');
            copyStatus.className = 'featured-photo-copy-status';
            copyStatus.setAttribute('role', 'status');
            copyStatus.setAttribute('aria-live', 'polite');
            copyButton.addEventListener('pointerdown', event => event.stopPropagation());
            copyButton.addEventListener('click', event => {
                event.stopPropagation();
                request.resetHoldTimer?.();
                request.insights?.mark('linkCopied');
                const path = createFeaturedImagePath(photoTitle);
                const writeText = navigator.clipboard?.writeText.bind(navigator.clipboard);
                if (writeText == null) {
                    copyStatus.textContent = siteStrings.featuredPhotoLinkCopyFailed;
                    return;
                }
                void writeText(new URL(path, window.location.origin).href).then(
                    () => {
                        if (featureRequest === request) copyStatus.textContent = siteStrings.featuredPhotoLinkCopied;
                    },
                    () => {
                        if (featureRequest === request) copyStatus.textContent = siteStrings.featuredPhotoLinkCopyFailed;
                    });
            });
            const shop = config?.shop;
            // Only public photographs are sold, so a standalone private photograph has no order button.
            if (shop?.enabled === true && selectedTile.standalone !== true) {
                const price = formatPrintPrice(shop.unitPriceCents, shop.currency);
                const orderButton = document.createElement('button');
                orderButton.type = 'button';
                orderButton.className = 'featured-photo-order-print';
                orderButton.textContent = formatSiteString(siteStrings.orderPrint, { price });
                orderButton.setAttribute('aria-label', formatSiteString(siteStrings.orderPrintLabel, { title: photoTitle, price }));
                orderButton.addEventListener('pointerdown', event => event.stopPropagation());
                orderButton.addEventListener('click', event => {
                    event.stopPropagation();
                    request.insights?.mark('orderClicked');
                    void startPrintCheckout(
                        photoTitle,
                        orderButton,
                        copyStatus,
                        () => featureRequest === request,
                        holdFeatureOpen(request),
                        () => endFeatureInsights(request, 'ordered'));
                });
                actions.appendChild(orderButton);
            }
            actions.append(copyButton, copyStatus);
            caption.appendChild(actions);
        }
        if (caption.childElementCount > 0) {
            feature.append(content, caption);
            request.caption = caption;
        } else {
            feature.appendChild(content);
        }
        featureLayer.appendChild(feature);
        featureLayer.classList.add('feature-layer-active');
        featureLayer.setAttribute('aria-hidden', 'false');
        request.element = feature;
        request.closeButton = closeButton;
        const cornerRadius = Number.parseFloat(getComputedStyle(selectedTile.element).borderTopLeftRadius) || 0;
        request.gestureState = {
            content,
            frame,
            cornerRadius,
            enabled: false,
            imageBounds,
            minimumScale: calculateFeaturedImageFitScale(
                placement.renderedWidth,
                placement.renderedHeight,
                window.innerWidth,
                window.innerHeight),
            pointers: new Map(),
            transform: { scale: 1, panX: 0, panY: 0 },
            gesture: null,
            suppressDismiss: false,
            quality: {
                frame,
                activeImage: preferredImage,
                preferredImage,
                preferredImageUrl,
                preferredImageIsOriginal: false,
                preferredStatus: 'idle',
                preferredOperation: null,
                preferredRevealSignal: null,
                originalImage: null,
                originalAttempt: null,
                originalOperation: null,
                fullResolutionImageUrl: selectedTile.photo.fullResolutionImageUrl,
                availableWidth: 0,
                availableHeight: 0,
                isOriginal: false,
                originalRequested: false
            }
        };
        const preferredImageReady = ensurePreferredFeaturedImage(request, request.gestureState, signal);

        if (addHistoryEntry) {
            const title = selectedTile.photo.title;
            const path = title != null && title.trim().length > 0
                ? createFeaturedImagePath(title)
                : undefined;
            window.history.pushState(createFeaturedImageHistoryState(request.imageKey), '', path);
            // The photo URL is what the visitor and crawlers see now, so retitle the tab before the reveal animates.
            updateDocumentTitle();
            request.hasActiveHistoryEntry = true;
            if (source === 'user' && path != null) siteTelemetry.trackPageView(path);
        }
        const endingTransform = formatViewportTransform({ scale: 1, panX: 0, panY: 0 });
        if (slideDirection == null) {
            const expansionDuration = reducedMotionQuery.matches ? 0 : config.featureExpandMilliseconds;
            const startingTransform = calculateFeaturedImageFrameTransform(
                imageBounds,
                startingRectangle ?? createStandaloneStartingRectangle(imageBounds));
            const backdropAnimation = feature.animate([
                { backgroundColor: 'rgb(5 5 5 / 0%)' },
                { backgroundColor: '#050505' }
            ], {
                duration: expansionDuration,
                easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
                fill: 'forwards'
            });
            const contentAnimation = content.animate([
                { transform: formatViewportTransform(startingTransform) },
                { transform: endingTransform }
            ], {
                duration: expansionDuration,
                easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
                fill: 'forwards'
            });
            const cornerAnimation = frame.animate(
                createCornerRadiusKeyframes(
                    startingTransform.scale,
                    1,
                    cornerRadius,
                    cornerRadius), {
                    duration: expansionDuration,
                    easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
                    fill: 'forwards'
                });
            request.animations.push(backdropAnimation, contentAnimation, cornerAnimation);

            await Promise.all([backdropAnimation.finished, contentAnimation.finished, cornerAnimation.finished]);
            signal.throwIfAborted();
            feature.style.backgroundColor = '#050505';
            frame.style.borderRadius = '0';
            backdropAnimation.cancel();
            contentAnimation.cancel();
            cornerAnimation.cancel();
        } else {
            // Navigation keeps the outgoing photograph on screen and slides it off the opposite
            // edge while the next photograph slides in, matching a film-strip transition. The
            // previous figure stays in the document so its opaque backdrop never exposes the grid.
            const slideDuration = reducedMotionQuery.matches ? 0 : featuredNavigationSlideMilliseconds;
            const startingTransform = formatViewportTransform({
                scale: 1,
                panX: slideDirection * window.innerWidth,
                panY: 0
            });
            if (previousRequest != null) {
                // Drop the outgoing presentation's caption so it does not linger over the slide;
                // the shared close and navigation buttons stay put like the playback toggle.
                previousRequest.caption?.remove();
                const previousContent = previousRequest.gestureState?.content;
                const previousStartingTransform = previousContent == null
                    ? endingTransform
                    : getComputedStyle(previousContent).transform;
                previousContent?.animate([
                    { transform: previousStartingTransform },
                    { transform: formatViewportTransform({ scale: 1, panX: -slideDirection * window.innerWidth, panY: 0 }) }
                ], {
                    duration: slideDuration,
                    easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
                    fill: 'forwards'
                });
                outgoingFeatureRequests.push(previousRequest);
            }
            frame.style.borderRadius = '0';
            const contentAnimation = content.animate([
                { transform: startingTransform },
                { transform: endingTransform }
            ], {
                duration: slideDuration,
                easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
                fill: 'forwards'
            });
            request.animations.push(contentAnimation);
            await contentAnimation.finished;
            signal.throwIfAborted();
            contentAnimation.cancel();
            // The new figure is opaque now, so every superseded presentation can be released
            // without ever exposing the grid.
            feature.style.backgroundColor = '#050505';
            const outgoingRequests = outgoingFeatureRequests;
            outgoingFeatureRequests = [];
            for (const outgoingRequest of outgoingRequests) finishFeaturePresentation(outgoingRequest);
        }
        content.style.transform = endingTransform;
        if (request.gestureState != null) request.gestureState.enabled = true;
        request.insights?.shown();
        showFeatureCloseButton(request, slideDirection == null);
        featureLayer.classList.add('featured-photo-nav-visible');
        await preferredImageReady;
        signal.throwIfAborted();
        showFeatureCaption(request);
        lastFeaturedImageKey = selectedTile.photo.imageKey;
        featuredImageCycle.markFeatured(selectedTile.photo.imageKey);
        await waitForFeatureInactivity(
            request,
            source === 'user' ? userFeatureHoldMilliseconds : config.featureHoldMilliseconds,
            signal);
        request.insightsEndReason ??= 'timed-out';
        requestFeatureDismissal(request);
    } catch (error) {
        if (!signal.aborted) console.warn('Featured photograph could not be presented.', error);
    } finally {
        // A slide transition that never completed leaves its superseded presentations pending;
        // release them so their figures cannot linger behind the grid.
        if (slideDirection != null && featureRequest !== request && outgoingFeatureRequests.length > 0) {
            const pendingOutgoingRequests = outgoingFeatureRequests;
            outgoingFeatureRequests = [];
            for (const outgoingRequest of pendingOutgoingRequests) {
                if (outgoingRequest !== request) finishFeaturePresentation(outgoingRequest);
            }
        }
        if (request.controller.signal === signal
            && !request.isClosing
            && !request.isReopening
            && !request.isAwaitingHistoryDismissal) {
            if (request.hasActiveHistoryEntry) requestFeatureDismissal(request);
            else finishFeaturePresentation(request);
        }
    }
}

function centerRenderedTileForImageKey(imageKey: string): RenderedTile | null {
    const matchingTiles = renderedTiles.filter(tile => tile.photo.imageKey === imageKey && tile.element.isConnected);
    const selection = selectFeatureCenteringAdjustment(
        matchingTiles.map(tile => tile.element.getBoundingClientRect()),
        window.innerWidth,
        window.innerHeight,
        sequenceHeight);
    if (selection == null) return null;

    momentumVelocity = 0;
    automaticScrollVelocity = 0;
    scrollOffset = advanceLoopOffset(scrollOffset, selection.scrollAdjustment, sequenceHeight);
    track.style.transform = `translate3d(0, ${-scrollOffset}px, 0)`;
    // The scroll offset wraps by one full sequence height, so the chosen tile element can end
    // up one loop away from the viewport while an identical copy of the same photograph sits
    // centered on screen. Re-select from the post-scroll positions so the returned tile is
    // always the copy that is actually visible.
    const postScrollRectangles = matchingTiles.map(tile => tile.element.getBoundingClientRect());
    const visibleRectangle = selectFeatureReturnRectangle(
        postScrollRectangles,
        window.innerWidth,
        window.innerHeight);
    if (visibleRectangle == null) return matchingTiles[selection.rectangleIndex] ?? null;
    const visibleIndex = postScrollRectangles.findIndex(rectangle =>
        rectangle.left === visibleRectangle.left && rectangle.top === visibleRectangle.top);
    return visibleIndex >= 0
        ? matchingTiles[visibleIndex]
        : matchingTiles[selection.rectangleIndex] ?? null;
}

async function openDirectLinkedFeature(slug: string): Promise<void> {
    let resolution: { collectionSlug: string; isPublic: boolean; imageKey: string } | null = null;
    try {
        resolution = await fetchJson(`/api/images/${encodeURIComponent(slug)}/collection`) as { collectionSlug: string; isPublic: boolean; imageKey: string };
    } catch {
        resolution = null;
    }

    window.history.replaceState(null, '', '/');
    if (resolution == null) {
        scheduleNextAutomaticFeature();
        return;
    }

    if (!resolution.isPublic) {
        await openStandaloneFeature(resolution.collectionSlug, resolution.imageKey);
        return;
    }

    // Public deep link: switch (and keep) the selector to this collection.
    linkedCollectionSlug = null;
    adminCollectionSlug = resolution.collectionSlug;
    if (!await requestRefresh(true)) {
        scheduleNextAutomaticFeature();
        return;
    }

    const tile = centerRenderedTileForImageKey(resolution.imageKey);
    if (tile == null) {
        scheduleNextAutomaticFeature();
        return;
    }

    const canonicalTitle = tile.photo.title;
    if (canonicalTitle == null || canonicalTitle.trim().length === 0) {
        scheduleNextAutomaticFeature();
        return;
    }

    window.history.pushState(
        createFeaturedImageHistoryState(tile.photo.imageKey),
        '',
        createFeaturedImagePath(canonicalTitle));
    // presentFeature is called without a new history entry here, so this push is the only URL change to track.
    updateDocumentTitle();
    void presentFeature(tile, 'user', 'link', false);
}

/**
 * Opens a photograph that is only in private collections over the home page's own wall. It has no tile there, so it
 * grows from the middle of the screen and fades away on close, and its private collection is never shown.
 *
 * @param collectionSlug - A private collection that holds the photograph.
 * @param imageKey - The photograph to open.
 */
async function openStandaloneFeature(collectionSlug: string, imageKey: string): Promise<void> {
    let photo: PublicPhoto | undefined;
    try {
        const privateCollection = normalizeCollection(await fetchJson(`/api/collections/${encodeURIComponent(collectionSlug)}`));
        photo = privateCollection.images.find(image => image.imageKey === imageKey);
    } catch {
        photo = undefined;
    }

    const title = photo?.title;
    if (photo == null || title == null || title.trim().length === 0) {
        scheduleNextAutomaticFeature();
        return;
    }

    const tile: RenderedTile = {
        photo,
        element: document.createElement('div'),
        image: document.createElement('img'),
        rowKey: '',
        standalone: true
    };
    window.history.pushState(createFeaturedImageHistoryState(imageKey), '', createFeaturedImagePath(title));
    // presentFeature makes this the open presentation before its first await, so the tab title can name it.
    void presentFeature(tile, 'user', 'link', false);
    updateDocumentTitle();
}

/** Where a standalone photograph grows from: its open position, shrunk toward the middle of the screen. */
function createStandaloneStartingRectangle(imageBounds: ImageBounds): Rectangle {
    const scale = 0.85;
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    const left = centerX + (imageBounds.left - centerX) * scale;
    const top = centerY + (imageBounds.top - centerY) * scale;
    const width = imageBounds.width * scale;
    const height = imageBounds.height * scale;
    return { left, top, width, height, right: left + width, bottom: top + height };
}

function openUserFeature(tile: RenderedTile): void {
    cancelAutomaticFeatureTimer();
    const request = featureRequest;
    if (request?.isClosing && request.imageKey === tile.photo.imageKey) {
        // Catching the closing photograph keeps the same presentation open, and counts as interacting with it.
        request.insights?.mark('interacted');
        void reopenClosingFeature(request);
        return;
    }

    void presentFeature(tile, 'user', 'tile');
}

/**
 * Creates a full-screen viewer button that steps to the neighboring photograph.
 *
 * The button renders a stroked SVG chevron instead of a plain "<" / ">" text character so its glyph
 * matches the playback and close buttons and the content can never be selected or dragged as text.
 *
 * @param direction - -1 for the previous photograph, 1 for the next one.
 * @param positionClass - The CSS class that places the button on the correct screen edge.
 * @param label - The accessible label describing the button.
 */
function createFeaturedPhotoNavButton(direction: -1 | 1, positionClass: string, label: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `featured-photo-nav ${positionClass}`;
    button.setAttribute('aria-label', label);
    // createElementNS is required here: an "svg" element produced by document.createElement stays an
    // unknown HTML element in the DOM and would never render.
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    const glyph = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    glyph.setAttribute('d', direction < 0 ? 'M14.5 5.5 9 12l5.5 6.5' : 'M9.5 5.5 15 12l-5.5 6.5');
    icon.appendChild(glyph);
    button.appendChild(icon);
    button.addEventListener('pointerdown', event => event.stopPropagation());
    button.addEventListener('click', event => {
        event.stopPropagation();
        navigateFeaturedImage(direction);
    });
    return button;
}

/**
 * Moves the open featured photograph to the next or previous image in the collection order,
 * wrapping around at either end and keeping the address bar and browser history in sync.
 *
 * The existing history entry is reused in place whenever the current presentation already owns
 * one, so closing the viewer still returns straight to the grid instead of stepping back through
 * every navigated photograph.
 *
 * @param direction - -1 for the previous photograph, 1 for the next one.
 */
function navigateFeaturedImage(direction: -1 | 1): void {
    const request = featureRequest;
    const images = collection?.images ?? [];
    if (request == null || request.isReopening || request.sourceTile.standalone === true || images.length < 2) return;

    // If the close animation is already running, reverse it back to the open photograph
    // first; the recursive call then performs the normal slide navigation. Clicks during
    // the reversal are ignored by the isReopening guard above.
    if (request.isClosing) {
        void (async () => {
            if (await reverseClosingFeature(request)) navigateFeaturedImage(direction);
        })();
        return;
    }

    const currentIndex = images.findIndex(image => image.imageKey === request.imageKey);
    const startingIndex = currentIndex < 0 ? 0 : currentIndex;
    const nextImage = images[(startingIndex + direction + images.length) % images.length];

    const tile = centerRenderedTileForImageKey(nextImage.imageKey)
        ?? findRenderedTileForHistory(nextImage.imageKey);
    if (tile == null) return;

    const reuseCurrentHistoryEntry = request.hasActiveHistoryEntry;
    // The outgoing presentation ends now, before the slide, so each photograph is reported once and its time on
    // screen stops when the visitor moved on. The arrow (or swipe) is an interaction with the photograph left.
    request.insights?.mark('interacted');
    endFeatureInsights(request, 'navigated');
    void presentFeature(tile, 'user', 'arrow', !reuseCurrentHistoryEntry, direction);
    if (!reuseCurrentHistoryEntry) return;

    const title = nextImage.title;
    const path = title != null && title.trim().length > 0
        ? createFeaturedImagePath(title)
        : undefined;
    window.history.replaceState(createFeaturedImageHistoryState(nextImage.imageKey), '', path);
    updateDocumentTitle();
}

function getTouchDistance(first: TouchPoint, second: TouchPoint): number {
    return Math.hypot(second.x - first.x, second.y - first.y);
}

function cancelZoomResetTimer(): void {
    if (zoomResetTimeoutId == null) return;
    window.clearTimeout(zoomResetTimeoutId);
    zoomResetTimeoutId = null;
}

function scheduleZoomReset(): void {
    cancelZoomResetTimer();
    if (pinchItemsPerRow == null) return;
    zoomResetTimeoutId = window.setTimeout(() => {
        zoomResetTimeoutId = null;
        pinchItemsPerRow = null;
        queueZoomRender();
    }, gridZoomResetMilliseconds);
}

function noteUserInteraction(): void {
    if (pinchItemsPerRow == null || pinchGesture != null || pinchAwaitingRelease) return;
    scheduleZoomReset();
}

function scrollWallWithWheel(event: WheelEvent): void {
    if (featureRequest != null || sequenceHeight <= 0) return;

    const delta = normalizeWheelDelta(event.deltaY, event.deltaMode, wall.clientHeight);
    if (delta === 0) return;

    event.preventDefault();
    cancelAutomaticFeatureTimer();
    if (event.ctrlKey) {
        momentumVelocity = 0;
        pinchItemsPerRow = calculateWheelItemsPerRow(
            pinchItemsPerRow ?? getTargetItemsPerRow(wall.clientWidth, wall.clientHeight),
            delta,
            minimumPinchItemsPerRow,
            maximumPinchItemsPerRow);
        queueZoomRender();
        scheduleZoomReset();
        scheduleNextAutomaticFeature();
        return;
    }

    noteUserInteraction();
    if (reducedMotionQuery.matches) {
        momentumVelocity = 0;
        scrollOffset = advanceLoopOffset(scrollOffset, delta, sequenceHeight);
        track.style.transform = `translate3d(0, ${-scrollOffset}px, 0)`;
        return;
    }

    momentumVelocity = calculateWheelMomentumVelocity(
        momentumVelocity,
        delta,
        wheelMomentumPerPixel,
        maximumMomentumVelocity);
}

function beginWallDrag(event: PointerEvent): void {
    if (event.button !== 0 || featureRequest != null || sequenceHeight <= 0) return;

    if (event.pointerType === 'touch') {
        activeTouchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (activeTouchPointers.size === 2) {
            const pointerEntries = [...activeTouchPointers.entries()];
            const first = pointerEntries[0];
            const second = pointerEntries[1];
            const startingDistance = getTouchDistance(first[1], second[1]);
            if (startingDistance > 0) {
                cancelAutomaticFeatureTimer();
                cancelZoomResetTimer();
                momentumVelocity = 0;
                wallDrag = null;
                pinchAwaitingRelease = true;
                pinchGesture = {
                    pointerIds: [first[0], second[0]],
                    startingDistance,
                    startingItemsPerRow: pinchItemsPerRow ?? getTargetItemsPerRow(wall.clientWidth, wall.clientHeight)
                };
                for (const pointerId of pinchGesture.pointerIds) wall.setPointerCapture(pointerId);
                suppressNextTileClick = true;
                event.preventDefault();
                return;
            }
        }
    }

    if (pinchGesture != null) return;
    cancelAutomaticFeatureTimer();
    momentumVelocity = 0;
    wallDrag = {
        pointerId: event.pointerId,
        startingY: event.clientY,
        startingOffset: scrollOffset,
        hasMoved: false,
        lastY: event.clientY,
        lastTimestamp: event.timeStamp,
        velocity: 0
    };
}

function moveWallDrag(event: PointerEvent): void {
    if (event.pointerType === 'touch' && activeTouchPointers.has(event.pointerId)) {
        activeTouchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    }

    if (pinchGesture != null && pinchGesture.pointerIds.includes(event.pointerId)) {
        const first = activeTouchPointers.get(pinchGesture.pointerIds[0]);
        const second = activeTouchPointers.get(pinchGesture.pointerIds[1]);
        if (first != null && second != null) {
            const currentDistance = getTouchDistance(first, second);
            if (currentDistance > 0) {
                pinchItemsPerRow = calculatePinchItemsPerRow(
                    pinchGesture.startingItemsPerRow,
                    pinchGesture.startingDistance,
                    currentDistance,
                    minimumPinchItemsPerRow,
                    maximumPinchItemsPerRow);
                queueZoomRender();
            }
        }
        event.preventDefault();
        return;
    }

    const drag = wallDrag;
    if (drag == null || drag.pointerId !== event.pointerId) return;

    const distance = event.clientY - drag.startingY;
    if (!drag.hasMoved && Math.abs(distance) < dragThresholdPixels) return;
    if (!drag.hasMoved) wall.setPointerCapture(event.pointerId);
    drag.hasMoved = true;
    const elapsedSeconds = (event.timeStamp - drag.lastTimestamp) / 1000;
    if (elapsedSeconds > 0) {
        const instantaneousVelocity = -(event.clientY - drag.lastY) / elapsedSeconds;
        drag.velocity = drag.velocity * 0.65 + instantaneousVelocity * 0.35;
    }
    drag.lastY = event.clientY;
    drag.lastTimestamp = event.timeStamp;
    scrollOffset = advanceLoopOffset(drag.startingOffset, -distance, sequenceHeight);
    track.style.transform = `translate3d(0, ${-scrollOffset}px, 0)`;
    lastAnimationTimestamp = 0;
    event.preventDefault();
}

function endWallDrag(event: PointerEvent): void {
    if (event.pointerType === 'touch') activeTouchPointers.delete(event.pointerId);
    if (pinchGesture != null && pinchGesture.pointerIds.includes(event.pointerId)) {
        pinchGesture = null;
        wallDrag = null;
        if (wall.hasPointerCapture(event.pointerId)) wall.releasePointerCapture(event.pointerId);
        lastAnimationTimestamp = 0;
        event.preventDefault();
        return;
    }

    if (event.pointerType === 'touch' && pinchAwaitingRelease) {
        if (wall.hasPointerCapture(event.pointerId)) wall.releasePointerCapture(event.pointerId);
        if (activeTouchPointers.size === 0) {
            pinchAwaitingRelease = false;
            scheduleZoomReset();
            scheduleNextAutomaticFeature();
            window.setTimeout(() => { suppressNextTileClick = false; }, 0);
        }
        event.preventDefault();
        return;
    }

    const drag = wallDrag;
    if (drag == null || drag.pointerId !== event.pointerId) return;

    wallDrag = null;
    if (wall.hasPointerCapture(event.pointerId)) wall.releasePointerCapture(event.pointerId);
    lastAnimationTimestamp = 0;

    if (!drag.hasMoved) {
        scheduleNextAutomaticFeature();
        return;
    }

    momentumVelocity = reducedMotionQuery.matches || Math.abs(drag.velocity) < minimumMomentumVelocity
        ? 0
        : Math.max(-maximumMomentumVelocity, Math.min(maximumMomentumVelocity, drag.velocity));
    if (momentumVelocity === 0) scheduleNextAutomaticFeature();
    suppressNextTileClick = true;
    window.setTimeout(() => { suppressNextTileClick = false; }, 0);
}

function readFiniteNumber(candidate: Record<string, unknown>, propertyName: string): number {
    const value = candidate[propertyName];
    if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error(`Configuration property '${propertyName}' is invalid.`);
    return value;
}

function normalizeSiteConfig(payload: unknown): SiteConfig {
    if (payload == null || typeof payload !== 'object') throw new Error('Site configuration was malformed.');
    const candidate = payload as Record<string, unknown>;
    if (candidate.collectionSlug !== null && typeof candidate.collectionSlug !== 'string') {
        throw new Error('Site configuration was incomplete.');
    }
    if (candidate.zoomMode !== 'zoomToPoi' && candidate.zoomMode !== 'zoomToFit') {
        throw new Error('Site configuration contained an invalid zoom mode.');
    }

    return {
        collectionSlug: candidate.collectionSlug,
        zoomMode: candidate.zoomMode,
        scrollPixelsPerSecond: readFiniteNumber(candidate, 'scrollPixelsPerSecond'),
        featureIntervalSeconds: readFiniteNumber(candidate, 'featureIntervalSeconds'),
        featureExpandMilliseconds: readFiniteNumber(candidate, 'featureExpandMilliseconds'),
        featureHoldMilliseconds: readFiniteNumber(candidate, 'featureHoldMilliseconds'),
        featureCaptionHoldMilliseconds: readFiniteNumber(candidate, 'featureCaptionHoldMilliseconds'),
        featureFadeMilliseconds: readFiniteNumber(candidate, 'featureFadeMilliseconds'),
        collectionRefreshSeconds: readFiniteNumber(candidate, 'collectionRefreshSeconds'),
        shop: normalizeShopConfig(candidate.shop)
    };
}

function normalizePhoto(payload: unknown): PublicPhoto | null {
    if (payload == null || typeof payload !== 'object') return null;
    const candidate = payload as Record<string, unknown>;
    const stringProperties = ['imageKey', 'thumbnailImageUrl', 'imageUrl', 'fullResolutionImageUrl'] as const;
    const numberProperties = ['previewWidth', 'previewHeight', 'fullResolutionWidth', 'fullResolutionHeight', 'poiX', 'poiY', 'sortOrder'] as const;
    if (stringProperties.some(propertyName => typeof candidate[propertyName] !== 'string' || candidate[propertyName].length === 0)
        || (candidate.title != null && typeof candidate.title !== 'string')
        || (candidate.description != null && typeof candidate.description !== 'string')
        || (candidate.altText != null && typeof candidate.altText !== 'string')
        || (candidate.displayResolutionImageUrl != null
            && (typeof candidate.displayResolutionImageUrl !== 'string' || candidate.displayResolutionImageUrl.length === 0))
        || numberProperties.some(propertyName => typeof candidate[propertyName] !== 'number' || !Number.isFinite(candidate[propertyName]))) {
        return null;
    }

    const photo = candidate as unknown as PublicPhoto;
    if (photo.previewWidth <= 0 || photo.previewHeight <= 0 || photo.fullResolutionWidth <= 0 || photo.fullResolutionHeight <= 0
        || photo.poiX < 0 || photo.poiX > 1 || photo.poiY < 0 || photo.poiY > 1 || !Number.isSafeInteger(photo.sortOrder)) {
        return null;
    }

    return {
        ...photo,
        title: typeof candidate.title === 'string' ? candidate.title : null,
        description: typeof candidate.description === 'string' ? candidate.description : null,
        altText: typeof candidate.altText === 'string' ? candidate.altText : null,
        thumbnailImageUrl: new URL(photo.thumbnailImageUrl, window.location.origin).href,
        imageUrl: new URL(photo.imageUrl, window.location.origin).href,
        fullResolutionImageUrl: new URL(photo.fullResolutionImageUrl, window.location.origin).href,
        displayResolutionImageUrl: typeof candidate.displayResolutionImageUrl === 'string'
            ? new URL(candidate.displayResolutionImageUrl, window.location.origin).href
            : null
    };
}

function normalizeCollection(payload: unknown): PublicCollection {
    if (payload == null || typeof payload !== 'object') throw new Error('Collection response was malformed.');
    const candidate = payload as Record<string, unknown>;
    if (typeof candidate.name !== 'string' || typeof candidate.slug !== 'string'
        || typeof candidate.lastModifiedUtc !== 'string' || !Array.isArray(candidate.images)) {
        throw new Error('Collection response was incomplete.');
    }

    const images = candidate.images.map(image => normalizePhoto(image));
    if (images.some(image => image == null)) throw new Error('Collection response contained an invalid image.');
    return { name: candidate.name, slug: candidate.slug, lastModifiedUtc: candidate.lastModifiedUtc, images: images as PublicPhoto[] };
}

function normalizeAdminCollectionSummaries(payload: unknown): AdminCollectionSummary[] {
    if (!Array.isArray(payload)) throw new Error('Collection summaries response was malformed.');

    return payload.map(item => {
        if (item == null || typeof item !== 'object') throw new Error('Collection summary was malformed.');
        const candidate = item as Record<string, unknown>;
        if (typeof candidate.name !== 'string' || candidate.name.length === 0
            || typeof candidate.slug !== 'string' || candidate.slug.length === 0) {
            throw new Error('Collection summary was incomplete.');
        }

        return {
            name: candidate.name,
            slug: candidate.slug,
            isPublic: typeof candidate.isPublic === 'boolean' ? candidate.isPublic : true
        };
    });
}

async function fetchJson(url: string): Promise<unknown> {
    let response: Response;
    try {
        response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    } catch (error) {
        throw new SiteConnectionError(error);
    }

    if (!response.ok) throw new Error(`Request failed with status ${response.status}.`);
    return response.json() as Promise<unknown>;
}

function showStatus(message: string, kind: 'status' | 'error' = 'status'): void {
    statusSurface.textContent = message;
    statusSurface.dataset.kind = kind;
    statusSurface.hidden = false;
}

function hideStatus(): void {
    statusSurface.hidden = true;
}

function isMobilePhone(): boolean {
    const userAgentData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
    if (userAgentData?.mobile !== undefined) {
        return userAgentData.mobile;
    }

    return window.matchMedia('(max-width: 768px)').matches
        && window.matchMedia('(pointer: coarse)').matches;
}

function getTargetItemsPerRow(viewportWidth: number, viewportHeight: number): number {
    if (pinchItemsPerRow != null) return pinchItemsPerRow;
    if (mobilePhone) return phoneTargetItemsPerRow;
    if (viewportHeight >= viewportWidth) return portraitTargetItemsPerRow;

    const scaledTarget = Math.round(viewportWidth / desktopReferenceWidthPixels * desktopTargetItemsPerRow);
    const defaultTarget = Math.min(Math.max(scaledTarget, minimumTargetItemsPerRow), maximumTargetItemsPerRow);
    return defaultTarget;
}

function queueZoomRender(): void {
    if (zoomRenderFrameId !== 0) return;
    zoomRenderFrameId = requestAnimationFrame(() => {
        zoomRenderFrameId = 0;
        renderCollection();
    });
}

function setStyleProperty(style: CSSStyleDeclaration, property: 'left' | 'top' | 'width' | 'height', value: string): void {
    if (style[property] !== value) style[property] = value;
}

function getPhotoAltText(photo: PublicPhoto): string {
    return photo.altText?.trim() ?? '';
}

function getPhotoTileLabel(photo: PublicPhoto): string {
    const name = getPhotoAltText(photo) || photo.title?.trim();
    return name ? `Open photograph in full view: ${name}` : 'Open photograph in full view';
}

function getPhotoTilePath(photo: PublicPhoto): string | null {
    const title = photo.title?.trim();
    if (title == null || title.length === 0) return null;
    try {
        return createFeaturedImagePath(title);
    } catch {
        return null;
    }
}

function updateTile(tile: RenderedTile, occurrence: HomepageTileOccurrence<PublicPhoto>): void {
    tile.photo = occurrence.photo;
    tile.rowKey = occurrence.rowKey;
    tile.element.dataset.imageKey = occurrence.photo.imageKey;
    const path = getPhotoTilePath(occurrence.photo);
    if (path == null) {
        tile.element.removeAttribute('href');
        tile.element.setAttribute('role', 'button');
        tile.element.tabIndex = 0;
    } else {
        if (tile.element.getAttribute('href') !== path) tile.element.setAttribute('href', path);
        tile.element.removeAttribute('role');
        tile.element.removeAttribute('tabindex');
    }
    const label = getPhotoTileLabel(occurrence.photo);
    if (tile.element.getAttribute('aria-label') !== label) tile.element.setAttribute('aria-label', label);
    const altText = getPhotoAltText(occurrence.photo);
    if (tile.image.alt !== altText) tile.image.alt = altText;
    setStyleProperty(tile.element.style, 'left', `${occurrence.left}px`);
    setStyleProperty(tile.element.style, 'top', `${occurrence.top}px`);
    setStyleProperty(tile.element.style, 'width', `${occurrence.width}px`);
    setStyleProperty(tile.element.style, 'height', `${occurrence.height}px`);
    if (tile.image.src !== occurrence.photo.thumbnailImageUrl) tile.image.src = occurrence.photo.thumbnailImageUrl;
}

function createTile(occurrence: HomepageTileOccurrence<PublicPhoto>): RenderedTile {
    const tile = document.createElement('a');
    tile.className = 'photo-tile';
    tile.draggable = false;
    tile.setAttribute('aria-label', getPhotoTileLabel(occurrence.photo));

    const image = new Image();
    image.alt = getPhotoAltText(occurrence.photo);
    image.decoding = 'async';
    image.loading = 'lazy';
    tile.appendChild(image);
    const renderedTile = { photo: occurrence.photo, element: tile, image, rowKey: occurrence.rowKey };
    tile.addEventListener('dragstart', event => event.preventDefault());
    tile.addEventListener('keydown', event => {
        if (event.repeat || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
        if (event.key === ' ' || (event.key === 'Enter' && !tile.hasAttribute('href'))) {
            event.preventDefault();
            tile.click();
        }
    });
    tile.addEventListener('click', event => {
        if (tile.hasAttribute('href') &&
            (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)) return;
        event.preventDefault();
        const activation = resolveHomepageTileActivation(suppressNextTileClick, pinchAwaitingRelease);
        suppressNextTileClick = activation.suppressNextTileClick;
        if (!activation.shouldOpen) return;

        openUserFeature(renderedTile);
    });
    return renderedTile;
}

function getRenderInputs(): RenderInputs {
    const viewportWidth = Math.max(1, wall.clientWidth);
    const viewportHeight = Math.max(1, wall.clientHeight);
    const collectionRevision = collection == null
        ? ''
        : `${collection.slug}:${collection.lastModifiedUtc}:${collection.images.length}`;
    return {
        viewportWidth,
        viewportHeight,
        targetItemsPerRow: getTargetItemsPerRow(viewportWidth, viewportHeight),
        collectionRevision
    };
}

function matchesRenderInputs(left: RenderInputs | null, right: RenderInputs): boolean {
    return left != null
        && left.viewportWidth === right.viewportWidth
        && left.viewportHeight === right.viewportHeight
        && left.targetItemsPerRow === right.targetItemsPerRow
        && left.collectionRevision === right.collectionRevision;
}

function removeRenderedContent(): void {
    for (const tile of renderedTilesByOccurrenceKey.values()) tile.element.remove();
    for (const sequence of sequenceElements.values()) sequence.remove();
    renderedTiles = [];
    renderedTilesByOccurrenceKey.clear();
    sequenceElements.clear();
}

/** Generation counter that invalidates pending swap timers when a newer transition starts. */
let collectionSwapGeneration = 0;

/**
 * Eases the photo wall out, applies the collection swap while it is invisible, then eases it back in.
 * Anchoring the fade at data arrival (not at the user's selection) keeps the total transition time exact
 * regardless of network latency, and any render that interleaves during the window still happens while
 * the wall is fully faded, so a resize or a second selection can never flash new tiles in early.
 *
 * @param applySwap - Applies the new collection layout; invoked while the wall is hidden.
 * @returns A promise that resolves once the swap has been applied, so callers that need the
 * swapped tiles (the direct photo link) can wait for them without racing the fade timer.
 */
function runCollectionSwapWithFade(applySwap: () => void): Promise<void> {
    if (reducedMotionQuery.matches) {
        applySwap();
        return Promise.resolve();
    }

    const generation = ++collectionSwapGeneration;
    const halfDuration = collectionTransitionMilliseconds * 0.5;
    wall.style.transition = `opacity ${halfDuration}ms ease-in`;
    wall.style.opacity = '0';
    return new Promise<void>(resolve => {
        window.setTimeout(() => {
            // A newer transition superseded this swap, so its render was skipped; settle the
            // promise anyway so the waiting refresh cannot block on it forever.
            if (generation !== collectionSwapGeneration) {
                resolve();
                return;
            }
            applySwap();
            // The new collection's tiles now exist, so anything waiting on them can proceed;
            // the fade-in below is purely visual and runs independently.
            resolve();
            // One frame lets the swapped tiles paint before the fade-in begins.
            requestAnimationFrame(() => {
                if (generation !== collectionSwapGeneration) return;
                wall.style.transition = `opacity ${halfDuration}ms ease-out`;
                wall.style.opacity = '1';
            });
        }, halfDuration);
    });
}

function renderCollection(cancelFeatureOnLayoutChange = false): void {
    const renderInputs = getRenderInputs();
    if (matchesRenderInputs(lastRenderInputs, renderInputs)) return;
    lastRenderInputs = renderInputs;
    if (cancelFeatureOnLayoutChange) {
        const consumeFeatureHistoryEntry = featureRequest?.hasActiveHistoryEntry === true;
        cancelFeaturePresentationImmediately();
        if (consumeFeatureHistoryEntry) window.history.back();
    }

    const previousSequenceHeight = sequenceHeight;
    const previousProgress = previousSequenceHeight > 0 ? scrollOffset / previousSequenceHeight : 0;
    const images = collection?.images ?? [];
    if (images.length === 0) {
        removeRenderedContent();
        sequenceHeight = 0;
        showStatus('This collection does not contain any photographs yet.');
        return;
    }

    const { viewportWidth, viewportHeight, targetItemsPerRow } = renderInputs;
    const usableWidth = Math.max(1, viewportWidth - horizontalMarginPixels * 2);
    const hasFixedDefaultDensity = pinchItemsPerRow == null && (mobilePhone || viewportHeight >= viewportWidth);
    const minimumItemsPerRow = pinchItemsPerRow == null
        ? hasFixedDefaultDensity ? targetItemsPerRow : Math.max(minimumTargetItemsPerRow, targetItemsPerRow - 1)
        : Math.max(minimumPinchItemsPerRow, Math.floor(targetItemsPerRow));
    const maximumItemsPerRow = pinchItemsPerRow == null
        ? hasFixedDefaultDensity ? targetItemsPerRow : Math.min(maximumTargetItemsPerRow, targetItemsPerRow + 1)
        : Math.min(maximumPinchItemsPerRow, Math.ceil(targetItemsPerRow));
    const targetRowHeight = Math.max(1, (usableWidth - gapPixels * Math.max(0, targetItemsPerRow - 1))
        / Math.max(0.1, targetItemsPerRow * fallbackAspectRatio));
    const completeLoop = calculateCompleteLoopLayout(
        images,
        usableWidth,
        targetRowHeight,
        minimumItemsPerRow,
        maximumItemsPerRow);
    const layout = completeLoop.layout;
    sequenceHeight = layout.contentHeight + gapPixels;
    scrollOffset = previousProgress * sequenceHeight;
    const sequenceCount = Math.max(3, Math.ceil(viewportHeight / sequenceHeight) + 2);
    const occurrences: HomepageTileOccurrence<PublicPhoto>[] = [];

    for (let sequenceIndex = 0; sequenceIndex < sequenceCount; sequenceIndex++) {
        const occurrenceOrdinals = new Map<string, number>();

        for (const item of layout.items) {
            const photo = completeLoop.images[item.itemIndex];
            const occurrenceOrdinal = occurrenceOrdinals.get(photo.imageKey) ?? 0;
            occurrenceOrdinals.set(photo.imageKey, occurrenceOrdinal + 1);
            occurrences.push({
                key: createHomepageTileOccurrenceKey(sequenceIndex, photo.imageKey, occurrenceOrdinal),
                photo,
                rowKey: `${sequenceIndex}:${item.rowIndex}`,
                left: viewportWidth * 0.5 + item.centerX - item.width * 0.5,
                top: -item.centerY - item.height * 0.5,
                width: item.width,
                height: item.height
            });
        }
    }

    const reconciliation = reconcileHomepageTileOccurrences(
        occurrences,
        renderedTilesByOccurrenceKey,
        createTile,
        updateTile);
    renderedTiles = reconciliation.records;
    renderedTilesByOccurrenceKey = reconciliation.recordsByKey;

    const tilesPerSequence = layout.items.length;
    for (let sequenceIndex = 0; sequenceIndex < sequenceCount; sequenceIndex++) {
        let sequence = sequenceElements.get(sequenceIndex);
        if (sequence == null) {
            sequence = document.createElement('section');
            sequence.className = 'photo-sequence';
            sequence.dataset.sequenceIndex = String(sequenceIndex);
            sequenceElements.set(sequenceIndex, sequence);
            track.appendChild(sequence);
        }

        const sequenceHeightStyle = `${sequenceHeight}px`;
        const sequenceTransform = `translateY(${sequenceIndex * sequenceHeight}px)`;
        if (sequence.style.height !== sequenceHeightStyle) sequence.style.height = sequenceHeightStyle;
        if (sequence.style.transform !== sequenceTransform) sequence.style.transform = sequenceTransform;

        const firstTileIndex = sequenceIndex * tilesPerSequence;
        for (let tileIndex = 0; tileIndex < tilesPerSequence; tileIndex++) {
            const tile = renderedTiles[firstTileIndex + tileIndex].element;
            if (sequence.children[tileIndex] !== tile) sequence.insertBefore(tile, sequence.children[tileIndex] ?? null);
        }
    }

    for (const tile of reconciliation.obsoleteRecords) tile.element.remove();
    for (const [sequenceIndex, sequence] of sequenceElements) {
        if (sequenceIndex < sequenceCount) continue;
        sequence.remove();
        sequenceElements.delete(sequenceIndex);
    }
    track.style.transform = `translate3d(0, ${-scrollOffset}px, 0)`;
    document.getElementById('initial-photo-links')?.remove();
    hideStatus();
}

function animateWall(timestamp: number): void {
    const elapsedSeconds = lastAnimationTimestamp === 0
        ? 0
        : Math.min(Math.max((timestamp - lastAnimationTimestamp) / 1000, 0), 0.1);
    lastAnimationTimestamp = timestamp;

    if (!document.hidden && !reducedMotionQuery.matches && wallDrag == null && config != null && sequenceHeight > 0) {
        const automaticScrollAcceleration = config.scrollPixelsPerSecond / automaticScrollTransitionSeconds;
        if (featureRequest != null) requestFeatureGridPauseIfNeeded(featureRequest);

        const targetAutomaticScrollVelocity = !isHomepagePlaybackPlaying
            || featureRequest?.pauseGridScrolling === true
            && featureRequest.isClosing === false
            ? 0
            : config.scrollPixelsPerSecond;
        const nextAutomaticScrollVelocity = approachVelocity(
            automaticScrollVelocity,
            targetAutomaticScrollVelocity,
            automaticScrollAcceleration,
            elapsedSeconds);
        const averageAutomaticScrollVelocity = (automaticScrollVelocity + nextAutomaticScrollVelocity) * 0.5;
        const activeMomentumVelocity = momentumVelocity;
        scrollOffset = advanceLoopOffset(
            scrollOffset,
            (averageAutomaticScrollVelocity + activeMomentumVelocity) * elapsedSeconds,
            sequenceHeight);
        automaticScrollVelocity = nextAutomaticScrollVelocity;
        track.style.transform = `translate3d(0, ${-scrollOffset}px, 0)`;

        if (activeMomentumVelocity !== 0) {
            momentumVelocity = decelerateVelocity(activeMomentumVelocity, momentumDeceleration, elapsedSeconds);
            if (momentumVelocity === 0) scheduleNextAutomaticFeature();
        }
    }

    requestAnimationFrame(animateWall);
}

/**
 * Shows print-shop links and wording only while the shop is enabled, and fills the Prints page with the
 * configured price, ship time, and return window so the page never disagrees with checkout.
 */
function applyShopConfig(shop: ShopConfig): void {
    for (const element of document.querySelectorAll<HTMLElement>('[data-shop-only]')) element.hidden = !shop.enabled;
    printsUnavailable.hidden = shop.enabled;
    printsDetails.hidden = !shop.enabled;
    if (!shop.enabled) return;

    const fieldValues: Record<string, string> = {
        price: formatPrintPrice(shop.unitPriceCents, shop.currency),
        'ship-days': String(shop.shipWithinBusinessDays),
        'return-days': String(shop.returnWindowBusinessDays)
    };
    for (const element of document.querySelectorAll<HTMLElement>('[data-shop-field]')) {
        const value = fieldValues[element.dataset.shopField ?? ''];
        if (value != null) element.textContent = value;
    }
}

async function applyRefreshedState(nextConfig: SiteConfig, nextCollection: PublicCollection | null): Promise<void> {
    config = nextConfig;
    applyShopConfig(nextConfig.shop);
    if (nextCollection == null) {
        featuredImageCycle.reset([]);
        collection = null;
        updateDocumentTitle();
        renderCollection();
        showStatus('No collection has been selected for the public site.');
        return;
    }

    // Switching to a different collection must render even when its revision fields coincide
    // with the previous collection's (equal lastModifiedUtc and image counts), or the wall
    // would keep the old collection's tiles and a direct photo link could never find its tile.
    const isCollectionChange = collection != null && collection.slug !== nextCollection.slug;
    const shouldRender = hasCollectionChanged(collection, nextCollection) || isCollectionChange;
    const imageKeys = nextCollection.images.map(image => image.imageKey);
    if (collection?.slug === nextCollection.slug) featuredImageCycle.reconcile(imageKeys);
    else featuredImageCycle.reset(imageKeys);
    const isCollectionSwap = shouldRender && collection != null;
    collection = nextCollection;
    updateDocumentTitle();
    if (shouldRender) {
        if (isCollectionSwap) await runCollectionSwapWithFade(() => renderCollection());
        else renderCollection();
    }
}

function requestRefresh(allowHidden: boolean): Promise<boolean> {
    if (!allowHidden && document.hidden) return Promise.resolve(false);

    return refreshTask.tryRun(() => runRefreshCycle({
        allowHidden,
        isHidden: () => document.hidden,
        loadConfig: async () => normalizeSiteConfig(await fetchJson('/api/site-config')),
        getCollectionSlug: nextConfig => linkedCollectionSlug ?? adminCollectionSlug ?? pendingDirectCollectionSlug ?? nextConfig.collectionSlug,
        loadCollection: async collectionSlug => {
            try {
                return normalizeCollection(await fetchJson(`/api/collections/${encodeURIComponent(collectionSlug)}`));
            } catch (error) {
                // A direct-linked collection that disappeared between resolution and load, or a private collection
                // named by a stale campaign link, must not surface as a startup failure; fall back to the default
                // collection so the site still renders once.
                if (collectionSlug !== pendingDirectCollectionSlug && collectionSlug !== linkedCollectionSlug) throw error;
                pendingDirectCollectionSlug = null;
                linkedCollectionSlug = null;
                const defaultSlug = config?.collectionSlug ?? null;
                if (defaultSlug == null) throw error;
                return normalizeCollection(await fetchJson(`/api/collections/${encodeURIComponent(defaultSlug)}`));
            }
        },
        applyState: applyRefreshedState
    }));
}

// The public and private collections are separate groups in the navigation, divided by a dash line that never
// receives focus.
const collectionNavigationSeparatorLabel = '────────────────────';

/**
 * Rebuilds the home-page collection navigation: the public collections sorted alphabetically, an optional divider,
 * and finally the private collections sorted alphabetically. Anonymous visitors only ever receive public
 * collections, so they keep seeing a single alphabetical list without a divider.
 *
 * @param summaries - Collection summaries to show; a missing visibility is treated as public.
 */
function populateCollectionNavigation(summaries: AdminCollectionSummary[]): void {
    collectionNavigationList.replaceChildren();

    // The server already orders by name, but sorting each group here keeps the grouping stable for every data source.
    const publicSummaries = summaries
        .filter(summary => summary.isPublic !== false)
        .sort(compareCollectionNames);
    const privateSummaries = summaries
        .filter(summary => summary.isPublic === false)
        .sort(compareCollectionNames);

    appendCollectionLinks(publicSummaries);

    // The divider only makes sense when a collection exists on both sides of it.
    if (publicSummaries.length > 0 && privateSummaries.length > 0) {
        const separator = document.createElement('li');
        separator.className = 'collection-navigation-separator';
        separator.setAttribute('aria-hidden', 'true');
        separator.textContent = collectionNavigationSeparatorLabel;
        collectionNavigationList.append(separator);
    }

    appendCollectionLinks(privateSummaries);
    updateCollectionNavigationActiveState();
}

/**
 * Compares two collection summaries by name using a case-insensitive comparison, matching the server-side
 * `ORDER BY Name COLLATE NOCASE` ordering.
 *
 * @param left - First collection summary.
 * @param right - Second collection summary.
 * @returns A negative number when the left summary sorts first, a positive number when the right one does.
 */
function compareCollectionNames(left: AdminCollectionSummary, right: AdminCollectionSummary): number {
    return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
}

/**
 * Appends one link per collection summary to the collection navigation. The links use the canonical collection
 * paths, so they stay real navigation even before the page script runs.
 *
 * @param summaries - The already ordered collection summaries to append.
 */
function appendCollectionLinks(summaries: AdminCollectionSummary[]): void {
    for (const summary of summaries) {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = createCollectionPath(summary.name, summary.slug);
        link.textContent = summary.name;
        link.dataset.collectionSlug = summary.slug;
        item.append(link);
        collectionNavigationList.append(item);
    }
}

/// Marks the link for the rendered collection as the current entry so the highlight stays on the collection
/// that is actually on the wall while a different switch is still loading.
function updateCollectionNavigationActiveState(): void {
    const activePath = collection == null ? null : createCollectionPath(collection.name, collection.slug);
    for (const link of collectionNavigationList.querySelectorAll<HTMLAnchorElement>('a[data-collection-slug]')) {
        const isActive = activePath != null && link.pathname.toLowerCase() === activePath.toLowerCase();
        if (isActive) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
    }
}

async function initializeCollectionNavigation(): Promise<void> {
    try {
        const sessionPayload = await fetchJson('/api/admin/auth/session');
        const isAdministrator = sessionPayload != null && typeof sessionPayload === 'object'
            && (sessionPayload as Record<string, unknown>).authenticated === true;
        if (isAdministrator) {
            // The public header exposes the administration shortcut only to a signed-in administrator.
            adminLink.hidden = false;
            adminSeparator.hidden = false;
            const summaries = normalizeAdminCollectionSummaries(await fetchJson('/api/admin/collection-summaries'));
            if (summaries.length === 0) return;

            adminCollectionSummaries = summaries;
            populateCollectionNavigation(summaries);
            updateDocumentTitle();
            return;
        }

        // Anonymous visitors only see public collections. The navigation replaces the label only when more than one
        // public collection exists; the default collection is always public so there is always at least one.
        const publicSummaries = normalizeAdminCollectionSummaries(await fetchJson('/api/collections'));
        if (publicSummaries.length <= 1) return;

        publicCollections = publicSummaries;
        populateCollectionNavigation(publicSummaries);
        updateDocumentTitle();
    } catch (error) {
        console.warn('Collection navigation initialization failed.', error);
    }
}

/**
 * Attaches the administrator-only minimize controls to the site header, the Collections picker, and the
 * collection title pill. Each control restores its own persisted state, so signed-in administrators keep
 * their panel layout across visits while other visitors never see the controls.
 */
function initializePanelCollapseControls(): void {
    void initPanelCollapse({
        storageKey: 'panel-collapse:site-header',
        panel: siteHeader,
        button: createPanelCollapseButton('Minimize the site header', 'Restore the site header'),
        collapseLabel: 'Minimize the site header',
        expandLabel: 'Restore the site header'
    });
    void initPanelCollapse({
        storageKey: 'panel-collapse:collections-picker',
        panel: collectionNavigation,
        button: createPanelCollapseButton('Minimize the Collections picker', 'Restore the Collections picker'),
        collapseLabel: 'Minimize the Collections picker',
        expandLabel: 'Restore the Collections picker'
    });
    void initPanelCollapse({
        storageKey: 'panel-collapse:collection-title',
        panel: collectionTitle,
        button: createPanelCollapseButton('Minimize the collection title', 'Restore the collection title'),
        collapseLabel: 'Minimize the collection title',
        expandLabel: 'Restore the collection title'
    });
}

async function selectCollection(
    collectionSlug: string,
    historyMode: 'push' | 'replace' | 'none' = 'push'
): Promise<void> {
    const previousSlug = adminCollectionSlug;
    linkedCollectionSlug = null;
    adminCollectionSlug = collectionSlug;
    setCollectionNavigationBusy(true);
    try {
        while (!await requestRefresh(true)) {
            await new Promise(resolve => window.setTimeout(resolve, 0));
        }
        hideStatus();
        updateCollectionPath(historyMode);
    } catch (error) {
        adminCollectionSlug = previousSlug;
        updateCollectionNavigationActiveState();
        showStatus(siteStrings.collectionLoadFailed, 'error');
        console.warn('Collection selection failed.', error);
    } finally {
        setCollectionNavigationBusy(false);
    }
}

/// Applies a collection navigation selection: releases any open photograph, discards the automatic open that
/// belonged to the replaced collection, and restarts a full countdown once the new wall has rendered.
///
/// @param collectionSlug - The slug carried by the clicked navigation link.
async function applyCollectionNavigationChange(collectionSlug: string): Promise<void> {
    if (collectionSlug.length === 0 || collectionNavigationBusy) return;

    // Changing collections swaps every photo on the wall, so an open featured photograph must not be left
    // floating over unrelated content; release it immediately (no collapse animation). A click-opened
    // photograph owns a history entry, which is consumed so Back returns to the previous collection
    // instead of the now-stale image entry (same handling as renderCollection(true)).
    const consumeFeatureHistoryEntry = featureRequest?.hasActiveHistoryEntry === true;
    cancelFeaturePresentationImmediately();
    if (consumeFeatureHistoryEntry) window.history.back();
    // The armed automatic open targets the collection that is being replaced, so it is dropped here;
    // restarting it only after the render keeps a stale timer from firing the instant the wall swaps.
    cancelAutomaticFeatureTimer();
    await selectCollection(collectionSlug);
    scheduleNextAutomaticFeature();
}

/**
 * Builds the canonical public path for a collection. The name-derived slug is preferred, matching the server rule,
 * and the stored slug is kept when the name cannot be encoded.
 *
 * @param name - The collection name, or null when no collection is loaded.
 * @param fallbackSlug - The stored slug used when the name has no URL-friendly characters.
 * @returns The absolute public path for the collection.
 */
function createCollectionPath(name: string | null, fallbackSlug: string): string {
    if (name != null) {
        try {
            const slug = createUrlSlug(name);
            if (slug.length > 0) return `/${slug}`;
        } catch {
            // Falling through keeps the stored slug when the name cannot be encoded.
        }
    }

    return fallbackSlug.length > 0 ? `/${fallbackSlug}` : '/';
}

/**
 * Mirrors the displayed collection into the address bar so collection links stay shareable and the back button can
 * return to the previously viewed collection.
 *
 * @param historyMode - Whether the path change pushes a history entry, replaces the current one, or is skipped.
 */
function updateCollectionPath(historyMode: 'push' | 'replace' | 'none'): void {
    if (historyMode === 'none') return;
    const path = createCollectionPath(collection?.name ?? null, collection?.slug ?? '');
    if (window.location.pathname === path) return;
    if (historyMode === 'push') window.history.pushState(null, '', path);
    else window.history.replaceState(null, '', path);
}

/**
 * Resolves a requested collection slug through the public collection list using the same precedence as the server
 * resolver: administrator-defined slugs first, then name-derived slugs.
 *
 * @param slug - The slug taken from the request path.
 * @returns The stored slug of the matching public collection, or null when nothing matches.
 */
async function resolvePublicCollectionSlug(slug: string): Promise<string | null> {
    const summaries = normalizeAdminCollectionSummaries(await fetchJson('/api/collections'));
    return findCollectionSlug(summaries, slug);
}

/**
 * Resolves a direct-linked collection slug for the initial render. Returns the canonical stored slug,
 * or null when the slug is not a known public collection. When the public list cannot be consulted, the
 * raw slug is returned so the collection load can still be attempted directly.
 *
 * @param slug - The collection slug taken from the request path.
 * @returns The slug to load on first refresh, or null when the slug is not a known public collection.
 */
async function resolveDirectCollectionSlug(slug: string): Promise<string | null> {
    try {
        return await resolvePublicCollectionSlug(slug);
    } catch (error) {
        console.warn('Collection deep link resolution failed; loading the raw slug.', error);
    }

    // The public list was unreachable, so fall back to loading the raw slug directly. If it 404s,
    // the first refresh surfaces its error normally rather than flashing the default collection.
    return slug;
}

/**
 * Resolves a /[collection-slug] link and selects the collection it names, canonicalizing the address bar.
 *
 * @param slug - The collection slug taken from the request path.
 */
async function openDirectLinkedCollection(slug: string): Promise<void> {
    let resolvedSlug: string | null;
    try {
        resolvedSlug = await resolvePublicCollectionSlug(slug);
    } catch (error) {
        console.warn('Collection deep link resolution failed.', error);
        resolvedSlug = null;
    }

    // Unknown and private slugs normally never reach the browser because the server redirects them to '/', but a
    // stale link, or a collection that disappeared between page loads, still falls back to the default collection.
    if (resolvedSlug == null) {
        window.history.replaceState(null, '', '/');
        return;
    }

    // A public collection deep link keeps the collection selected, exactly like a public /photo/ deep link does.
    await selectCollection(resolvedSlug, 'push');
}

/**
 * Re-selects the collection named by a restored history path without adding another history entry.
 *
 * @param slug - The collection slug taken from the restored path.
 */
async function syncCollectionFromPath(slug: string): Promise<void> {
    let resolvedSlug: string | null;
    try {
        resolvedSlug = await resolvePublicCollectionSlug(slug);
    } catch (error) {
        console.warn('Collection path synchronization failed.', error);
        return;
    }

    if (resolvedSlug == null || resolvedSlug === collection?.slug) return;
    await selectCollection(resolvedSlug, 'replace');
}

function requestBackgroundRefresh(): void {
    if (document.hidden) return;
    void requestRefresh(false).catch(error => console.warn('Collection refresh failed.', error));
}

function queueResize(): void {
    if (resizeFrameId !== 0) return;
    resizeFrameId = requestAnimationFrame(() => {
        resizeFrameId = 0;
        renderCollection(true);
    });
}

function findRenderedTileForHistory(imageKey: string): RenderedTile | null {
    const matchingTiles = renderedTiles.filter(tile => tile.photo.imageKey === imageKey && tile.element.isConnected);
    return matchingTiles.find(tile => isRectangleVisible(
        tile.element.getBoundingClientRect(),
        window.innerWidth,
        window.innerHeight)) ?? matchingTiles[0] ?? null;
}

function restoreFeaturedImageFromHistory(imageKey: string): void {
    if (!initializationCompleted || config == null || currentRoute !== 'home') return;

    const request = featureRequest;
    if (request?.imageKey === imageKey) {
        request.hasActiveHistoryEntry = true;
        request.isAwaitingHistoryDismissal = false;
        if (request.isClosing) void reopenClosingFeature(request);
        return;
    }

    if (request != null) cancelFeaturePresentationImmediately();
    const tile = findRenderedTileForHistory(imageKey);
    if (tile == null) {
        window.history.replaceState(null, '');
        scheduleNextAutomaticFeature();
        return;
    }

    void presentFeature(tile, 'user', 'history', false);
}

function handlePopState(event: PopStateEvent): void {
    const route = getSiteRoute(window.location.pathname);
    const imageKey = getFeaturedImageHistoryKey(event.state);
    void showSiteRoute(route, false, imageKey == null);
    if (imageKey == null) {
        const request = featureRequest;
        if (request != null && (request.hasActiveHistoryEntry || request.isAwaitingHistoryDismissal)) {
            request.hasActiveHistoryEntry = false;
            request.isAwaitingHistoryDismissal = false;
            if (config == null) finishFeaturePresentation(request);
            else void collapseFeature(request, reducedMotionQuery.matches ? 0 : config.featureFadeMilliseconds);
        }
        // Back/forward into a collection link re-selects the collection named by the restored path.
        const collectionSlug = getCollectionSlugFromPath(window.location.pathname);
        const isCurrentCollection = collectionSlug != null && collection != null
            && (collectionSlug.toLowerCase() === collection.slug.toLowerCase()
                || featuredImageSlugMatchesTitle(collectionSlug, collection.name));
        if (collectionSlug != null && !isCurrentCollection) void syncCollectionFromPath(collectionSlug);
        return;
    }

    if (route === 'home') restoreFeaturedImageFromHistory(imageKey);
    else window.history.replaceState(null, '');
}

function handlePageShow(event: PageTransitionEvent): void {
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (!isBackForwardPageShow(event.persisted, navigationEntry?.type)) return;

    scheduleNextAutomaticFeature();
}

/**
 * Takes the private collection a campaign link's scan opens from the address. The /q/ redirect names it in the
 * fragment, beside the campaign code that telemetry has already taken, because a private collection has no page.
 *
 * @returns The stored slug of the private collection, or null when the address names none.
 */
function takeLinkedCollectionSlugFromLocation(): string | null {
    if (!window.location.hash.startsWith('#')) return null;
    const parameters = new URLSearchParams(window.location.hash.slice(1));
    const slug = parameters.get('collection');
    if (slug == null) return null;
    parameters.delete('collection');
    const remaining = parameters.toString();
    window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}${remaining.length > 0 ? `#${remaining}` : ''}`);
    return slug.length > 0 ? slug : null;
}

async function start(): Promise<void> {
    try {
        // A link to a private collection shows it on the home page, without the public collection navigation,
        // until the visitor leaves; the collection is never listed anywhere.
        linkedCollectionSlug = takeLinkedCollectionSlugFromLocation();
        // Resolve a direct-linked collection from the path before the first refresh so the correct
        // collection is the first thing rendered, avoiding a flash of the default collection.
        const linkedImageSlug = getFeaturedImageSlugFromPath(window.location.pathname);
        const linkedCollectionPathSlug = linkedImageSlug == null
            ? getCollectionSlugFromPath(window.location.pathname)
            : null;
        if (linkedCollectionPathSlug != null) {
            pendingDirectCollectionSlug = await resolveDirectCollectionSlug(linkedCollectionPathSlug);
            if (pendingDirectCollectionSlug == null) {
                window.history.replaceState(null, '', '/');
            }
        }

        await requestRefresh(true);
        if (config == null) return;

        // The target collection for the initial render has been loaded; clear the pending slug so
        // subsequent background refreshes use the normal linked/admin/default precedence.
        pendingDirectCollectionSlug = null;

        initializationCompleted = true;
        window.setInterval(requestBackgroundRefresh, config.collectionRefreshSeconds * 1000);
        void initializeCollectionNavigation();
        initializePanelCollapseControls();
        if (linkedImageSlug != null) {
            await openDirectLinkedFeature(linkedImageSlug);
            return;
        }
        if (linkedCollectionPathSlug != null) {
            // The requested collection was already selected and rendered by the first refresh,
            // so only canonicalize the address bar from the resolved slug.
            if (pendingDirectCollectionSlug != null) updateCollectionPath('push');
            else await openDirectLinkedCollection(linkedCollectionPathSlug);
            return;
        }
        const historyImageKey = getFeaturedImageHistoryKey(window.history.state);
        if (historyImageKey == null) scheduleNextAutomaticFeature();
        else restoreFeaturedImageFromHistory(historyImageKey);
    } catch (error) {
        console.error('digitaljedwin.com startup failed.', error);
        if (error instanceof SiteConnectionError) document.body.dataset.connectionFailed = 'true';
        showStatus(error instanceof Error ? error.message : 'Unable to load photographs.', 'error');
    }
}

window.addEventListener('resize', queueResize, { passive: true });
window.addEventListener('popstate', handlePopState);
window.addEventListener('pageshow', handlePageShow);
// A photograph still open when the page goes away (tab closed, reload, link followed) is reported as page-hidden.
window.addEventListener('pagehide', () => photoInsights.endAll('page-hidden'));
// Wall exposure is sent once a minute, and when the page is hidden or goes away.
installWallExposureSampler(wallExposure, {
    setInterval: (callback, milliseconds) => window.setInterval(callback, milliseconds),
    window,
    document
});
window.visualViewport?.addEventListener('resize', queueResize, { passive: true });
window.visualViewport?.addEventListener('scroll', queueResize, { passive: true });
document.addEventListener('visibilitychange', () => {
    lastAnimationTimestamp = 0;
    if (document.hidden) cancelAutomaticFeatureTimer();
    else {
        scheduleNextAutomaticFeature();
        if (initializationCompleted) requestBackgroundRefresh();
    }
});
wall.addEventListener('pointerdown', beginWallDrag);
wall.addEventListener('pointermove', moveWallDrag);
wall.addEventListener('pointerup', endWallDrag);
wall.addEventListener('pointercancel', endWallDrag);
wall.addEventListener('wheel', scrollWallWithWheel, { passive: false });
reducedMotionQuery.addEventListener('change', () => {
    lastAnimationTimestamp = 0;
    if (reducedMotionQuery.matches) {
        automaticScrollVelocity = 0;
        momentumVelocity = 0;
    }
    scheduleNextAutomaticFeature();
});
featureLayer.addEventListener('pointerdown', beginFeaturedImageGesture);
featureLayer.addEventListener('pointermove', moveFeaturedImageGesture);
featureLayer.addEventListener('pointerup', endFeaturedImageGesture);
featureLayer.addEventListener('pointercancel', endFeaturedImageGesture);
featureLayer.addEventListener('lostpointercapture', endFeaturedImageGesture);
featureLayer.addEventListener('wheel', zoomFeaturedImageWithWheel, { passive: false });
// Press-and-drag on the zoomed photograph must pan; without this guard the browser starts a
// native image drag that cancels the captured pointer and aborts the gesture for the mouse.
featureLayer.addEventListener('dragstart', event => event.preventDefault());
document.addEventListener('pointerdown', noteUserInteraction, { passive: true });
document.addEventListener('keydown', noteUserInteraction);
// Escape works anywhere on the page so the featured photograph can always be closed from the keyboard.
document.addEventListener('keydown', handleFeatureKeydown);
for (const link of siteRouteLinks) {
    link.addEventListener('click', event => {
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        void showSiteRoute(getSiteRoute(link.pathname), true);
    });
}
aboutClose.addEventListener('click', () => void showSiteRoute('home', true));
subscribeClose.addEventListener('click', () => void showSiteRoute('home', true));
contactClose.addEventListener('click', () => void showSiteRoute('home', true));
rightsClose.addEventListener('click', () => void showSiteRoute('home', true));
orderThanksClose.addEventListener('click', () => void showSiteRoute('home', true));
printsClose.addEventListener('click', () => void showSiteRoute('home', true));
returnPolicyClose.addEventListener('click', () => void showSiteRoute('home', true));
subscribeForm.addEventListener('submit', event => void submitSubscriptionForm(event));
unsubscribeSubmit.addEventListener('click', () => void submitUnsubscribe());
contactForm.addEventListener('submit', event => void submitContactForm(event));
// The collection links are ordinary anchors, so modified clicks keep the browser default (new tab, download)
// and the script only takes over an unmodified left click. Re-picking the current collection does nothing,
// matching the old dropdown, which fired no change event for an unchanged value.
collectionNavigationList.addEventListener('click', event => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest<HTMLAnchorElement>('a[data-collection-slug]');
    if (link == null || link.getAttribute('aria-current') === 'page') return;
    event.preventDefault();
    void applyCollectionNavigationChange(link.dataset.collectionSlug ?? '');
});
homepagePlaybackToggle.addEventListener('click', toggleHomepagePlayback);
siteTelemetry.start(getTelemetryPath(currentRoute));
void showSiteRoute(currentRoute, false);
renderHomepagePlaybackControl();
initializeWindChimeControls();
requestAnimationFrame(animateWall);
void start();
